// mobile/screens/ExpensesList.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, FlatList, Alert, RefreshControl,
  ActivityIndicator, TextInput, Platform, ScrollView, Modal,
} from 'react-native';
import axios from 'axios';
import { API_BASE } from '../utils';
import { Ionicons } from '@expo/vector-icons';

import CreateSeasonModal from '../components/CreateSeasonModal';
import EnterpriseHeader from '../components/EnterpriseHeader';
import PadiProIcon from '../components/PadiProIcon';
import { PADIPRO, padiproShadow, padiproShadowLg } from '../components/padiproTheme';
import { useResponsive } from '../components/responsive';

const DEFAULT_CAT = ['pestisida','pupuk','gaji','rotari','tukang_tanam','parbabo','jetor','bibit','bikin_lapakbibit','keperluaan_rumah','panen','lainnya'];
const PEST_TYPES = ['insektisida','fungisida','herbisida','bakterisida','moluskisida'];
const PUPUK_TYPE_LABELS = {
  pupuk_semprot: 'Pupuk Semprot',
  urea: 'Urea',
  phoska: 'Phoska',
  phoska_plus: 'Phoska Plus',
  za: 'ZA',
  fertiphos: 'Fertiphos',
  tsp_tawon: 'TSP Tawon',
  dolomit: 'Dolomit',
  pupuk_lainnya: 'Pupuk Lainnya',
  tanpa_sub: 'Belum Terklasifikasi',
};
const PUPUK_BUSINESS_GROUPS = [
  {
    key: 'dasar',
    title: 'Pupuk Dasar',
    subtitle: 'Urea, Phoska, TSP, ZA, Fertiphos',
    icon: 'leaf-outline',
    color: '#4f7a3a',
    soft: 'rgba(79,122,58,0.10)',
    border: 'rgba(79,122,58,0.20)',
    keys: ['urea', 'phoska', 'phoska_plus', 'za', 'fertiphos', 'tsp_tawon'],
  },
  {
    key: 'semprot',
    title: 'Semprot / Mikro',
    subtitle: 'Pupuk daun, Zinc, Ultradap, mikro',
    icon: 'sparkles-outline',
    color: '#7d8a35',
    soft: 'rgba(125,138,53,0.11)',
    border: 'rgba(125,138,53,0.20)',
    keys: ['pupuk_semprot'],
  },
  {
    key: 'tanah',
    title: 'Pembenah Tanah',
    subtitle: 'Dolomit / kapur tanah',
    icon: 'earth-outline',
    color: '#8b6a35',
    soft: 'rgba(139,106,53,0.12)',
    border: 'rgba(139,106,53,0.20)',
    keys: ['dolomit'],
  },
  {
    key: 'lainnya',
    title: 'Lainnya',
    subtitle: 'Belum terklasifikasi',
    icon: 'file-tray-outline',
    color: '#747865',
    soft: 'rgba(116,120,101,0.10)',
    border: 'rgba(116,120,101,0.18)',
    keys: ['pupuk_lainnya', 'tanpa_sub'],
  },
];

// THEME
const COLORS = {
  bg: PADIPRO.bg, card: PADIPRO.surface, border: PADIPRO.border,
  text: PADIPRO.text, muted: PADIPRO.muted,
  accent: PADIPRO.primary, accentSoft: PADIPRO.primarySoft,
  sky: PADIPRO.sky, skySoft: PADIPRO.skySoft,
};
const R = { radius: 16, pad: 12 };
const shadow = padiproShadow;

const fmtIDR = n => `Rp ${Number(n||0).toLocaleString('id-ID')}`;
const fmtRante = (n) => Number(n || 0).toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtSignedIDR = (n) => {
  const v = Number(n || 0);
  const sign = v > 0 ? '+' : v < 0 ? '-' : '';
  return `${sign}Rp ${Math.abs(v).toLocaleString('id-ID')}`;
};
const toYMD = (d) => {
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const asLabel = (v='') => String(v || '').replace(/_/g, ' ').trim();
const asTitle = (v='') =>
  asLabel(v)
    .split(' ')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ''))
    .join(' ');
const normalizeKey = (v='') =>
  String(v || '')
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
const normalizeTypeKey = (v='') => normalizeKey(v).replace(/\s+/g, '_') || '(tanpa_sub)';
const asPupukTypeLabel = (v='') => PUPUK_TYPE_LABELS[normalizeTypeKey(v)] || asTitle(v);
const fmtDate = s => {
  const d = new Date(s);
  return new Intl.DateTimeFormat('id-ID', { day:'2-digit', month:'short', year:'numeric' }).format(d);
};
const catIcon = (c='') => {
  const k = String(c).toLowerCase();
  if (k === 'pestisida') return 'flask-outline';
  if (k === 'pupuk') return 'nutrition-outline';
  if (k === 'gaji' || k.includes('tukang')) return 'people-outline';
  if (k === 'bibit') return 'leaf-outline';
  if (k === 'rotari' || k === 'jetor') return 'construct-outline';
  if (k === 'parbabo') return 'car-outline';
  return 'wallet-outline';
};

const Chip = ({ label, active, onPress, color=COLORS.sky, soft=COLORS.skySoft, icon }) => (
  <TouchableOpacity onPress={onPress} style={[styles.chip, active && { borderColor: color, backgroundColor: soft }]} activeOpacity={0.85}>
    {!!icon && <Ionicons name={icon} size={14} color={active ? color : COLORS.muted} style={{ marginRight:6 }} />}
    <Text style={[styles.chipText, active && { color }]}>{label}</Text>
  </TouchableOpacity>
);

const buildSplitPreview = ({ total, method, fields }) => {
  const totalVal = Number(total || 0);
  const list = Array.isArray(fields) ? fields : [];
  const count = list.length;
  if (!(totalVal > 0) || count === 0) {
    return {
      rows: [],
      methodUsed: method === 'equal' ? 'equal' : 'area',
      invalidAreaCount: 0,
      totalAllocated: 0,
      denominator: 0,
      unitCost: 0,
      fallbackEqual: false,
    };
  }

  if (method === 'equal') {
    const each = totalVal / count;
    return {
      rows: list.map((f) => ({ id: f.id, name: f.name, area: Number(f.area_rante || 0), amount: each })),
      methodUsed: 'equal',
      invalidAreaCount: 0,
      totalAllocated: each * count,
      denominator: count,
      unitCost: each,
      fallbackEqual: false,
    };
  }

  const invalidAreaCount = list.filter((f) => !(Number(f.area_rante || 0) > 0)).length;
  if (invalidAreaCount > 0) {
    const each = totalVal / count;
    return {
      rows: list.map((f) => ({ id: f.id, name: f.name, area: Number(f.area_rante || 0), amount: each, note: 'rante kosong' })),
      methodUsed: 'equal',
      invalidAreaCount,
      totalAllocated: each * count,
      denominator: count,
      unitCost: each,
      fallbackEqual: true,
    };
  }

  const sumArea = list.reduce((acc, f) => acc + Number(f.area_rante || 0), 0);
  if (!(sumArea > 0)) {
    const each = totalVal / count;
    return {
      rows: list.map((f) => ({ id: f.id, name: f.name, area: Number(f.area_rante || 0), amount: each })),
      methodUsed: 'equal',
      invalidAreaCount: 0,
      totalAllocated: each * count,
      denominator: count,
      unitCost: each,
      fallbackEqual: true,
    };
  }

  const unitCost = totalVal / sumArea;
  return {
    rows: list.map((f) => {
      const area = Number(f.area_rante || 0);
      return { id: f.id, name: f.name, area, amount: unitCost * area };
    }),
    methodUsed: 'area',
    invalidAreaCount: 0,
    totalAllocated: totalVal,
    denominator: sumArea,
    unitCost,
    fallbackEqual: false,
  };
};

export default function ExpensesList({ route, navigation }) {
  const token = route?.params?.token;
  const refreshAt = route?.params?.refreshAt;
  const headers = useMemo(()=>({ Authorization: `Bearer ${token}` }),[token]);
  const { contentWidthStyle, isNarrow, isTiny } = useResponsive();

  // Mode: period | season
  const [mode, setMode] = useState('season');

  // Period state
  const [period, setPeriod] = useState('month'); // 'week'|'month'

  // Season state
  const [seasonList, setSeasonList] = useState([]);
  const activeSeason = useMemo(()=> (seasonList || []).find(s => !s.end_date) || null, [seasonList]);
  const [seasonId, setSeasonId] = useState(null);

  // Modal create season (dipisah supaya input gak kedip)
  const [createSeasonOpen, setCreateSeasonOpen] = useState(false);

  // Filters
  const [categoryOptions, setCategoryOptions] = useState(DEFAULT_CAT);
  const [category, setCategory] = useState(null);
  const [query, setQuery] = useState('');
  const queryNorm = useMemo(() => String(query || '').trim().toLowerCase(), [query]);

  const SORTS = ['Terbaru', 'Termahal', 'Nama'];
  const [sortIdx, setSortIdx] = useState(0);

  // Data
  const [items, setItems] = useState([]);
  const summaryDefaults = { grand_total: 0, items: [] };
  const [summary, setSummary] = useState(summaryDefaults);
  const [pestSummary, setPestSummary] = useState(summaryDefaults);
  const [pupukSummary, setPupukSummary] = useState(summaryDefaults);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [splitOpen, setSplitOpen] = useState(false);
  const [splitLoading, setSplitLoading] = useState(false);
  const [splitSubmitting, setSplitSubmitting] = useState(false);
  const [splitExpense, setSplitExpense] = useState(null);
  const [splitMethod, setSplitMethod] = useState('area');
  const [splitFields, setSplitFields] = useState([]);
  const [splitSelected, setSplitSelected] = useState([]);
  const [splitQuery, setSplitQuery] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkCategory, setBulkCategory] = useState(null);
  const [expandedAllocations, setExpandedAllocations] = useState({});

  const listRef = useRef(null);
  const fetchSeqRef = useRef(0);

  const normalizeFields = useCallback((rows) => {
    const list = Array.isArray(rows) ? rows : [];
    return list
      .map((x) => {
        const id = x.id ?? x.field_id ?? x.fieldId ?? null;
        const name = x.name ?? x.field_name ?? x.title ?? '';
        const area = x.area_rante ?? x.rante ?? x.luas_rante ?? x.rante_size ?? null;
        return {
          id: id != null ? Number(id) : null,
          name: String(name || ''),
          area_rante: area != null ? Number(area) : null,
        };
      })
      .filter((x) => x.id && x.name);
  }, []);

  // ===== Seasons API =====
  const fetchSeasons = useCallback(async ()=>{
    try {
      const r = await axios.get(`${API_BASE}/seasons`, { headers });
      const arr = Array.isArray(r.data) ? r.data : [];
      setSeasonList(arr);

      if (arr.length) {
        const act = arr.find(s => !s.end_date);
        if (act) setSeasonId(act.season_id);
        else setSeasonId(arr[0].season_id);
      } else {
        setSeasonId(null);
      }
    } catch (e) {
      console.warn('[SEASONS] gagal load:', e?.response?.data || e?.message);
    }
  }, [headers]);

  useEffect(()=>{ fetchSeasons(); }, [fetchSeasons]);

  const fetchCategoryOptions = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/expenses/categories`, { headers });
      const arr = Array.isArray(r?.data?.items) ? r.data.items : [];
      const keys = arr.map((it) => String(it?.key || '').trim()).filter(Boolean);
      setCategoryOptions(keys.length ? keys : DEFAULT_CAT);
    } catch {
      setCategoryOptions(DEFAULT_CAT);
    }
  }, [headers]);

  useEffect(() => {
    fetchCategoryOptions();
  }, [fetchCategoryOptions]);

  const fetchFieldsCompat = useCallback(async () => {
    if (!token) return [];
    setSplitLoading(true);
    try {
      const rMin = await axios.get(`${API_BASE}/operations/fields-min`, { headers });
      const rowsMin = rMin?.data?.fields || rMin?.data?.items || rMin?.data || [];
      const normMin = normalizeFields(rowsMin);
      if (normMin.length) return normMin;

      const rOps = await axios.get(`${API_BASE}/operations/fields`, { headers });
      const rowsOps = rOps?.data?.fields || rOps?.data?.items || rOps?.data || [];
      const normOps = normalizeFields(rowsOps);
      if (normOps.length) return normOps;

      const rLegacy = await axios.get(`${API_BASE}/fields`, { headers });
      const rowsLegacy = rLegacy?.data?.fields || rLegacy?.data?.items || rLegacy?.data || [];
      return normalizeFields(rowsLegacy);
    } catch {
      return [];
    } finally {
      setSplitLoading(false);
    }
  }, [headers, token, normalizeFields]);

  const openSplitModal = useCallback(async (expense) => {
    if (!expense) return;
    setSplitExpense(expense);
    setSplitMethod('area');
    setSplitSelected([]);
    setSplitQuery('');
    setSplitOpen(true);
    const fields = await fetchFieldsCompat();
    setSplitFields(fields);
  }, [fetchFieldsCompat]);

  const closeSplitModal = useCallback(() => {
    setSplitOpen(false);
    setSplitExpense(null);
    setSplitSelected([]);
    setSplitMethod('area');
    setSplitQuery('');
  }, []);

  const openBulkModal = useCallback(async () => {
    if (!categoryOptions.length) {
      Alert.alert('Info', 'Belum ada kategori yang tersedia.');
      return;
    }
    const initCat = category || categoryOptions[0] || null;
    setBulkCategory(initCat);
    setSplitMethod('area');
    setSplitSelected([]);
    setSplitQuery('');
    setBulkOpen(true);
    const fields = await fetchFieldsCompat();
    setSplitFields(fields);
  }, [category, categoryOptions, fetchFieldsCompat]);

  const closeBulkModal = useCallback(() => {
    setBulkOpen(false);
    setBulkCategory(null);
    setSplitSelected([]);
    setSplitMethod('area');
    setSplitQuery('');
  }, []);

  const confirmEndSeason = useCallback((name) => {
    return new Promise((resolve) => {
      if (Platform.OS === 'web') {
        // eslint-disable-next-line no-alert
        resolve(window.confirm(`Akhiri musim: ${name}?`));
        return;
      }
      Alert.alert(
        'Konfirmasi',
        `Akhiri musim: ${name}?`,
        [
          { text: 'Batal', style: 'cancel', onPress: ()=>resolve(false) },
          { text: 'Akhiri', style: 'destructive', onPress: ()=>resolve(true) },
        ]
      );
    });
  }, []);

  // ===== Range resolver =====
  const getRange = useCallback(()=>{
    if (mode === 'period') {
      const now = new Date();
      if (period === 'week') {
        const day = now.getDay()||7;
        const start = new Date(now); start.setDate(now.getDate() - (day-1));
        return { from: toYMD(start), to: toYMD(now), label: `Minggu ini • ${toYMD(start)}–${toYMD(now)}`, via: 'date' };
      }
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { from: toYMD(start), to: toYMD(now), label: `Bulan ini • ${toYMD(start)}–${toYMD(now)}`, via: 'date' };
    }

    // mode === 'season'
    if (seasonId) {
      const ss = seasonList.find(s=>s.season_id===seasonId);
      if (ss) {
        const nm = ss.name || `${ss.start_date}→${ss.end_date || '…'}`;
        return { season_id: seasonId, from: ss.start_date, to: ss.end_date, label: `Musim • ${nm}`, via: 'season' };
      }
    }

    // Jangan fallback ke rentang bulan saat daftar musim masih dimuat.
    // Respons rentang tanggal dapat membawa alokasi milik musim lain yang
    // kebetulan dibuat pada bulan berjalan, lalu menimpa hasil musim terpilih.
    return { label: "Memuat musim…", via: "season_pending" };
  }, [mode, period, seasonId, seasonList]);

  // ===== Fetch data (list + summaries) =====
  const fetchData = useCallback(async (opts={ silent:false })=>{
    const requestSeq = ++fetchSeqRef.current;
    try{
      if(!opts.silent) setLoading(true);
      const r = getRange();

      if (r.via === "season_pending") {
        setItems([]);
        setSummary(summaryDefaults);
        setPestSummary(summaryDefaults);
        setPupukSummary(summaryDefaults);
        return;
      }

      // Backend defaults to 200 rows; a season can easily hold 1000+ combined
      // expense + field_expense rows, so without this the list silently
      // truncates to the newest 200 instead of showing the whole season.
      const base = { ...(category ? { category } : {}), limit: 5000 };
      const listParams = r.via === 'season' ? { ...base, season_id: r.season_id } : { ...base, from: r.from, to: r.to };

      const reqList = axios.get(`${API_BASE}/expenses`, { params: listParams, headers });
      const reqSum = axios.get(`${API_BASE}/expenses/summary`, {
        params: { ...(r.via==='season'?{ season_id: r.season_id }:{ from:r.from, to:r.to }),
                  group: category ? 'subcategory' : 'category' },
        headers
      });
      const reqPest = axios.get(`${API_BASE}/expenses/summary`, {
        params: { ...(r.via==='season'?{ season_id: r.season_id }:{ from:r.from, to:r.to }),
                  category: 'pestisida', group: 'subcategory' },
        headers
      });
      const reqPupuk = axios.get(`${API_BASE}/expenses/summary`, {
        params: { ...(r.via==='season'?{ season_id: r.season_id }:{ from:r.from, to:r.to }),
                  category: 'pupuk', group: 'subcategory' },
        headers
      });

      const [resList, resSum, resPest, resPupuk] = await Promise.all([reqList, reqSum, reqPest, reqPupuk]);
      if (requestSeq !== fetchSeqRef.current) return;
      setItems(Array.isArray(resList.data) ? resList.data : []);
      setSummary(resSum.data || summaryDefaults);
      setPestSummary(resPest.data || summaryDefaults);
      setPupukSummary(resPupuk.data || summaryDefaults);
    }catch(e){
      if (requestSeq !== fetchSeqRef.current) return;
      Alert.alert('Error', e?.response?.data?.error || e?.message || 'Gagal memuat pengeluaran');
    }finally{
      if (requestSeq === fetchSeqRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [getRange, category, headers]);

  useEffect(()=>{ fetchData(); }, [fetchData]);
  useEffect(()=>{
    if (refreshAt) fetchData({ silent:true });
  }, [refreshAt, fetchData]);
  const onRefresh = useCallback(()=>{ setRefreshing(true); fetchData({ silent:true }); }, [fetchData]);

  // ===== Create season: via modal (preset/manual) =====
  const openCreateSeason = useCallback(()=>{
    setCreateSeasonOpen(true);
  }, []);

  const submitCreateSeason = useCallback(async ({ name, start_date, end_date })=>{
    try {
      // Kirim payload minimal: kalau user isi, kirim. Kalau kosong semua, backend bisa auto.
      const payload = {};
      if (name) payload.name = name;
      if (start_date) payload.start_date = start_date;
      if (end_date) payload.end_date = end_date;

      const r = await axios.post(`${API_BASE}/seasons`, payload, { headers });

      setCreateSeasonOpen(false);
      Alert.alert('Sukses', `Musim dimulai: ${r?.data?.season?.name || name || 'baru'}`);
      await fetchSeasons();
      await fetchData({ silent:true });
    } catch (e) {
      const status = e?.response?.status;
      const err = e?.response?.data;

      // Kalau backend kamu ternyata belum support start/end/name dan balikin 400,
      // kita fallback ke payload kosong (tetap "backend existing", user tetap bisa manual nama dari backend kalau sudah support).
      if (status === 400) {
        try {
          const r2 = await axios.post(`${API_BASE}/seasons`, {}, { headers });
          setCreateSeasonOpen(false);
          Alert.alert('Sukses', `Musim dimulai: ${r2?.data?.season?.name || 'baru'}`);
          await fetchSeasons();
          await fetchData({ silent:true });
          return;
        } catch (e2) {
          Alert.alert('Error', e2?.response?.data?.error || e2?.message || 'Gagal membuat musim');
          return;
        }
      }

      if (status === 409 && err?.code === 'ACTIVE_SEASON_EXISTS') {
        Alert.alert('Info', 'Masih ada musim aktif. Akhiri dulu sebelum mulai musim baru.');
      } else if (status === 409 && err?.code === 'SEASON_DATE_OVERLAP') {
        const minDate = err?.min_start_date ? `\nMulai paling cepat: ${err.min_start_date}` : '';
        Alert.alert('Tanggal musim bentrok', `${err?.error || 'Tanggal musim baru bentrok dengan musim sebelumnya.'}${minDate}`);
      } else if ([502, 503, 504].includes(status)) {
        Alert.alert('Server bermasalah', 'API sedang tidak stabil/down (gateway error). Coba lagi beberapa saat, lalu cek status backend dan database.');
      } else {
        Alert.alert('Error', err?.error || e?.message || 'Gagal membuat musim');
      }
    }
  }, [headers, fetchSeasons, fetchData]);

  const endActiveSeason = useCallback(async ()=>{
    try {
      if (!activeSeason?.season_id) {
        Alert.alert('Info', 'Tidak ada musim aktif.');
        return;
      }

      const ok = await confirmEndSeason(activeSeason.name || 'musim ini');
      if (!ok) return;

      const r = await axios.patch(`${API_BASE}/seasons/${activeSeason.season_id}/end`, {}, { headers });
      Alert.alert('Sukses', `Musim ${r.data?.season?.name || 'ini'} ditutup.`);
      await fetchSeasons();
      await fetchData({ silent:true });
    } catch (e) {
      const msg = e?.response?.data?.error || e.message;
      const reqId = e?.response?.data?.reqId;
      Alert.alert('Error', `${msg}${reqId ? `\nreqId: ${reqId}` : ''}`);
    }
  }, [activeSeason, headers, fetchSeasons, fetchData, confirmEndSeason]);

  // ===== Sort & stats =====
  const appliedItems = useMemo(()=>{
    let arr = [...items];

    if (queryNorm) {
      arr = arr.filter((it) => {
        const haystack = [
          it?.item_name,
          it?.notes,
          it?.category,
          it?.subcategory,
          it?.season_name,
          it?.field_name,
          it?.expense_date,
        ]
          .map((x) => String(x || '').toLowerCase())
          .join(' ');
        return haystack.includes(queryNorm);
      });
    }

    if (sortIdx === 0) arr.sort((a,b)=> new Date(b.expense_date) - new Date(a.expense_date));      // Terbaru
    else if (sortIdx === 1) arr.sort((a,b)=> Number(b.total||0) - Number(a.total||0));              // Termahal
    else arr.sort((a,b)=> (a.item_name||'').localeCompare(b.item_name||''));                        // Nama
    return arr;
  }, [items, sortIdx, queryNorm]);

  const displayItems = useMemo(() => {
    const grouped = new Map();
    const out = [];

    for (const item of appliedItems) {
      const src = String(item?.source_type || '').toLowerCase();
      const expenseId = Number(item?.expense_id || 0);
      const canGroupSplit = src === 'field_split';

      if (!canGroupSplit) {
        out.push(item);
        continue;
      }

      const fallbackKey = [
        item?.expense_date || '',
        item?.category || '',
        item?.subcategory || '',
        item?.item_name || '',
        item?.unit_price || '',
      ].join(':');
      const key = Number.isFinite(expenseId) && expenseId > 0
        ? `field_split_group:${expenseId}`
        : `field_split_group:${fallbackKey}`;
      let group = grouped.get(key);
      if (!group) {
        group = {
          ...item,
          source_type: 'field_split_group',
          group_key: key,
          field_expense_id: null,
          total: 0,
          quantity: null,
          field_name: null,
          allocation_count: 0,
          allocation_fields: [],
          allocation_rows: [],
        };
        grouped.set(key, group);
        out.push(group);
      }

      const amount = Number(item?.total || 0);
      const fieldName = String(item?.field_name || '').trim();
      group.total = Number(group.total || 0) + amount;
      group.allocation_count += 1;
      group.allocation_rows.push(item);
      if (fieldName && !group.allocation_fields.includes(fieldName)) {
        group.allocation_fields.push(fieldName);
      }

      const currentDate = new Date(group.expense_date || 0).getTime();
      const itemDate = new Date(item?.expense_date || 0).getTime();
      if (Number.isFinite(itemDate) && (!Number.isFinite(currentDate) || itemDate > currentDate)) {
        group.expense_date = item.expense_date;
      }
    }

    if (sortIdx === 0) {
      out.sort((a, b) => new Date(b.expense_date) - new Date(a.expense_date));
    } else if (sortIdx === 1) {
      out.sort((a, b) => Number(b.total || 0) - Number(a.total || 0));
    } else {
      out.sort((a, b) => (a.item_name || '').localeCompare(b.item_name || ''));
    }
    return out;
  }, [appliedItems, sortIdx]);

  const stats = useMemo(()=>{
    const count = appliedItems.length;
    const displayCount = displayItems.length;
    const total = Number(summary?.grand_total || 0);
    const r = getRange();
    let days = 1;

    if (r.from && r.to) {
      const to = r.to || toYMD(new Date());
      days = Math.max(1, Math.ceil((new Date(to) - new Date(r.from)) / 86400000) + 1);
    } else if (r.via==='season') {
      const ss = seasonList.find(s=>s.season_id===r.season_id);
      if (ss) {
        const end = ss.end_date || toYMD(new Date());
        days = Math.max(1, Math.ceil((new Date(end) - new Date(ss.start_date)) / 86400000) + 1);
      }
    }

    return { total, count, displayCount, avgPerDay: total / days, label: r.label, days };
  }, [appliedItems.length, displayItems.length, summary, getRange, seasonList]);

  const topSummary = useMemo(()=>{
    const total = Number(summary?.grand_total || 0);
    const itemsArr = Array.isArray(summary?.items) ? summary.items : [];
    const sorted = [...itemsArr].sort((a,b)=> Number(b.total||0) - Number(a.total||0));

    const topN = 5;
    const top = sorted.slice(0, topN);
    const rest = sorted.slice(topN);

    const restTotal = rest.reduce((acc, x)=> acc + Number(x.total||0), 0);
    const view = [...top];
    if (restTotal > 0) view.push({ key: 'Lainnya (gabungan)', total: restTotal, _others: true });

    const top3 = sorted.slice(0,3).reduce((acc,x)=> acc + Number(x.total||0), 0);
    const top5 = sorted.slice(0,5).reduce((acc,x)=> acc + Number(x.total||0), 0);

    const pct = (n) => total > 0 ? Math.round((Number(n||0) / total) * 100) : 0;

    return {
      total,
      view,
      top3Pct: pct(top3),
      top5Pct: pct(top5),
    };
  }, [summary]);

  const categorySubTopByKey = useMemo(() => {
    const catMap = new Map();
    const list = Array.isArray(appliedItems) ? appliedItems : [];
    for (const it of list) {
      const catKey = normalizeKey(it?.category || '(tanpa kategori)');
      const subKey = String(it?.subcategory || '').trim() || '(tanpa subkategori)';
      const amt = Number(it?.total || 0);
      if (!(amt > 0)) continue;
      if (!catMap.has(catKey)) catMap.set(catKey, new Map());
      const subMap = catMap.get(catKey);
      const prev = Number(subMap.get(subKey) || 0);
      subMap.set(subKey, prev + amt);
    }

    const out = {};
    for (const [catKey, subMap] of catMap.entries()) {
      const top3Subs = Array.from(subMap.entries())
        .map(([key, total]) => ({ key, total: Number(total || 0) }))
        .sort((a, b) => b.total - a.total)
        .slice(0, 3)
        .map((x) => asTitle(x.key));
      out[catKey] = top3Subs;
    }
    return out;
  }, [appliedItems]);

  const pupukFocus = useMemo(() => {
    const list = Array.isArray(appliedItems) ? appliedItems : [];
    const rows = list.filter((it) => normalizeKey(it?.category) === 'pupuk');
    const totalFromRows = rows.reduce((acc, it) => acc + Number(it?.total || 0), 0);
    const categoryTotal = Array.isArray(summary?.items)
      ? summary.items.find((x) => normalizeKey(x?.key) === 'pupuk')
      : null;
    const exactSummaryTotal = Number(category === 'pupuk' ? summary?.grand_total || 0 : categoryTotal?.total || 0);
    const total = exactSummaryTotal > 0 ? exactSummaryTotal : totalFromRows;
    const grandTotal = Number(category === 'pupuk' ? total : summary?.grand_total || 0);
    const pct = grandTotal > 0 ? Math.round((total / grandTotal) * 100) : 0;

    const typeRows = Array.isArray(pupukSummary?.items)
      ? pupukSummary.items
          .map((it) => ({
            key: it?.key || 'pupuk_lainnya',
            label: asPupukTypeLabel(it?.key || 'pupuk_lainnya'),
            total: Number(it?.total || 0),
            count: 0,
          }))
          .filter((it) => it.total > 0)
          .sort((a, b) => b.total - a.total)
      : [];

    const itemMap = new Map();
    for (const it of rows) {
      const key = String(it?.subcategory || it?.item_name || 'pupuk_lainnya').trim() || 'pupuk_lainnya';
      const prev = itemMap.get(key) || { key, total: 0, count: 0 };
      prev.total += Number(it?.total || 0);
      prev.count += 1;
      itemMap.set(key, prev);
    }
    const fallbackItems = Array.from(itemMap.values())
      .sort((a, b) => b.total - a.total)
      .slice(0, 3)
      .map((it) => ({
        ...it,
        label: asPupukTypeLabel(it.key),
        pct: total > 0 ? Math.round((Number(it.total || 0) / total) * 100) : 0,
      }));
    const topItems = (typeRows.length ? typeRows : fallbackItems)
      .slice(0, 3)
      .map((it) => ({
        ...it,
        label: it.label || asPupukTypeLabel(it.key),
        pct: total > 0 ? Math.round((Number(it.total || 0) / total) * 100) : 0,
      }));
    const dominant = topItems[0] || null;
    const totalsByType = new Map((typeRows.length ? typeRows : fallbackItems).map((it) => [normalizeTypeKey(it.key), Number(it.total || 0)]));
    const businessGroups = PUPUK_BUSINESS_GROUPS
      .map((group) => {
        const groupTotal = group.keys.reduce((acc, key) => acc + Number(totalsByType.get(key) || 0), 0);
        return {
          ...group,
          total: groupTotal,
          pct: total > 0 ? Math.round((groupTotal / total) * 100) : 0,
        };
      })
      .filter((group) => group.total > 0 || group.key !== 'lainnya')
      .sort((a, b) => b.total - a.total);

    return { total, pct, count: rows.length, typeCount: typeRows.length || fallbackItems.length, dominant, topItems, businessGroups };
  }, [appliedItems, category, summary, pupukSummary]);

  const pestTop = useMemo(()=>{
    const total = Number(pestSummary?.grand_total || 0);
    const itemsArr = Array.isArray(pestSummary?.items) ? pestSummary.items : [];

    const byType = PEST_TYPES
      .map(t => {
        const row = itemsArr.find(x => (x.key||'').toLowerCase() === t);
        return row ? { key: t, total: Number(row.total||0) } : null;
      })
      .filter(Boolean)
      .sort((a,b)=> b.total - a.total);

    const knownKeys = new Set(PEST_TYPES);
    const otherTotal = itemsArr.reduce((acc, x)=>{
      const k = (x.key||'').toLowerCase();
      if (!knownKeys.has(k)) return acc + Number(x.total||0);
      return acc;
    }, 0);

    const view = [...byType];
    if (otherTotal > 0) view.push({ key: 'lainnya (gabungan)', total: otherTotal, _others:true });

    const pct = (n) => total > 0 ? Math.round((Number(n||0)/total) * 100) : 0;

    return { total, view, pct };
  }, [pestSummary]);


  const splitSelectedFields = useMemo(() => {
    const map = new Map(splitFields.map((f) => [f.id, f]));
    return splitSelected.map((id) => map.get(id)).filter(Boolean);
  }, [splitFields, splitSelected]);
  const splitFilteredFields = useMemo(() => {
    const q = String(splitQuery || '').trim().toLowerCase();
    if (!q) return splitFields;
    return splitFields.filter((f) => {
      const hay = `${f.name || ''} ${f.area_rante || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [splitFields, splitQuery]);
  const splitStats = useMemo(() => {
    const selectedCount = splitSelectedFields.length;
    const selectedArea = splitSelectedFields.reduce((acc, f) => acc + Number(f.area_rante || 0), 0);
    const invalidAreaCount = splitSelectedFields.filter((f) => !(Number(f.area_rante || 0) > 0)).length;
    return { selectedCount, selectedArea, invalidAreaCount };
  }, [splitSelectedFields]);
  const bulkCategoryValue = bulkCategory || category || null;
  const bulkCandidates = useMemo(() => {
    const target = String(bulkCategoryValue || '').trim().toLowerCase();
    return appliedItems.filter((it) => {
      const src = String(it?.source_type || '').toLowerCase();
      const isSource = src === '' || src === 'expense';
      const sameCategory = target ? String(it?.category || '').trim().toLowerCase() === target : true;
      return isSource && sameCategory && Number(it?.expense_id || 0) > 0 && Number(it?.total || 0) > 0;
    });
  }, [appliedItems, bulkCategoryValue]);
  const bulkTotal = useMemo(
    () => bulkCandidates.reduce((acc, it) => acc + Number(it?.total || 0), 0),
    [bulkCandidates]
  );

  const splitPreview = useMemo(() => (
    buildSplitPreview({
      total: Number(splitExpense?.total || 0),
      method: splitMethod,
      fields: splitSelectedFields,
    })
  ), [splitExpense, splitMethod, splitSelectedFields]);
  const bulkPreview = useMemo(() => (
    buildSplitPreview({
      total: bulkTotal,
      method: splitMethod,
      fields: splitSelectedFields,
    })
  ), [bulkTotal, splitMethod, splitSelectedFields]);

  const toggleSplitField = useCallback((id) => {
    const fid = Number(id);
    if (!fid) return;
    setSplitSelected((prev) => (prev.includes(fid) ? prev.filter((x) => x !== fid) : [...prev, fid]));
  }, []);
  const selectAllSplitFields = useCallback(() => {
    setSplitSelected(splitFilteredFields.map((f) => Number(f.id)).filter(Boolean));
  }, [splitFilteredFields]);
  const clearSplitFields = useCallback(() => {
    setSplitSelected([]);
  }, []);

  const submitSplit = useCallback(async () => {
    if (!splitExpense) return;
    if (splitSelected.length === 0) {
      Alert.alert('Validasi', 'Pilih minimal 1 lahan.');
      return;
    }
    if (!(Number(splitExpense?.total || 0) > 0)) {
      Alert.alert('Validasi', 'Expense ini tidak punya total valid untuk dibagi.');
      return;
    }

    const payload = {
      expense_id: splitExpense.expense_id,
      expense_date: splitExpense.expense_date || null,
      category: splitExpense.category,
      subcategory: splitExpense.subcategory || null,
      item_name: splitExpense.item_name || null,
      quantity: splitExpense.quantity != null ? Number(splitExpense.quantity) : null,
      unit: splitExpense.unit || null,
      unit_price: splitExpense.unit_price != null ? Number(splitExpense.unit_price) : null,
      total: Number(splitExpense.total),
      notes: splitExpense.notes || null,
      pesticide_id: splitExpense.pesticide_id != null ? Number(splitExpense.pesticide_id) : null,
      field_ids: splitSelected,
      split_method: splitMethod,
    };

    try {
      setSplitSubmitting(true);
      await axios.post(`${API_BASE}/field-expenses/split`, payload, { headers });
      Alert.alert('Sukses', 'Biaya berhasil di-split ke lahan tanpa double total.');
      closeSplitModal();
      await fetchData({ silent: true });
    } catch (e) {
      Alert.alert('Error', e?.response?.data?.error || e?.message || 'Gagal split alokasi biaya');
    } finally {
      setSplitSubmitting(false);
    }
  }, [splitExpense, splitSelected, splitMethod, headers, closeSplitModal, fetchData]);

  const submitBulkSplit = useCallback(async () => {
    if (!bulkCategoryValue) {
      Alert.alert('Validasi', 'Pilih kategori untuk split massal.');
      return;
    }
    if (splitSelected.length === 0) {
      Alert.alert('Validasi', 'Pilih minimal 1 lahan.');
      return;
    }
    const r = getRange();
    const payload = {
      category: bulkCategoryValue,
      field_ids: splitSelected,
      split_method: splitMethod,
      max_expenses: 500,
      q: queryNorm || null,
      ...(r.via === 'season'
        ? { season_id: r.season_id }
        : { from: r.from, to: r.to }),
    };

    try {
      setBulkSubmitting(true);
      const res = await axios.post(`${API_BASE}/expenses/split-by-category`, payload, { headers });
      const data = res?.data || {};
      Alert.alert(
        'Split Massal Berhasil',
        `${data.expense_count || 0} expense di-split ke ${data.field_count || 0} lahan.\nTotal sumber: ${fmtIDR(data.total_source || 0)}`
      );
      closeBulkModal();
      await fetchData({ silent: true });
    } catch (e) {
      Alert.alert('Error', e?.response?.data?.error || e?.message || 'Gagal split massal kategori');
    } finally {
      setBulkSubmitting(false);
    }
  }, [bulkCategoryValue, splitSelected, bulkCandidates, getRange, splitMethod, queryNorm, headers, closeBulkModal, fetchData]);

  // ===== Header UI =====
  const renderHeader = () => (
    <View style={styles.headerWrap}>
      <View style={{ paddingHorizontal: R.pad, paddingTop: R.pad, paddingBottom: 6 }}>
          <EnterpriseHeader
            title="Pengeluaran PanenKu"
            subtitle="Kontrol biaya operasional lintas musim dan periode"
            right={(
              <View style={styles.headerActions}>
                <TouchableOpacity
                  onPress={()=>navigation.navigate('ReceiptScan', { token })}
                  style={styles.scanReceiptBtn}
                  activeOpacity={0.85}
                >
                  <Ionicons name="receipt-outline" size={15} color={COLORS.accent} />
                  <Text style={styles.scanReceiptText}>Scan</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={()=>navigation.navigate('AddExpense', { token })}
                  style={styles.addBtn}
                  activeOpacity={0.85}
                >
                  <Ionicons name="add" size={18} color="#fff" />
                </TouchableOpacity>
              </View>
            )}
          />
      </View>

      {/* Banner Musim */}
      {activeSeason ? (
        <View style={styles.banner}>
          <Ionicons name="leaf-outline" size={18} color={COLORS.accent} />
          <Text style={styles.bannerText}>
            Musim aktif: <Text style={{ fontWeight:'800' }}>{activeSeason.name}</Text> • {activeSeason.start_date} – …
          </Text>
          <TouchableOpacity onPress={endActiveSeason} style={styles.bannerBtn} activeOpacity={0.85}>
            <Text style={styles.bannerBtnText}>Panen (Akhiri Musim)</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <View style={[styles.banner, { borderColor: COLORS.sky, backgroundColor: COLORS.skySoft }]}>
          <Ionicons name="calendar-outline" size={18} color={COLORS.sky} />
          <Text style={styles.bannerText}>Belum ada musim aktif</Text>
          <TouchableOpacity onPress={openCreateSeason} style={[styles.bannerBtn, { backgroundColor: COLORS.sky }]} activeOpacity={0.85}>
            <Text style={styles.bannerBtnText}>Mulai Musim</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Mode */}
      <View style={styles.segmentRow}>
        <Chip label="Periode" icon="time-outline" color={COLORS.accent} soft={COLORS.accentSoft}
          active={mode==='period'} onPress={()=>{ setMode('period'); listRef.current?.scrollToOffset({ offset:0, animated:true }); }} />
        <Chip label="Musim Tanam" icon="leaf-outline" color={COLORS.accent} soft={COLORS.accentSoft}
          active={mode==='season'} onPress={()=>{ setMode('season'); listRef.current?.scrollToOffset({ offset:0, animated:true }); }} />
      </View>

      {/* Controls: Period or Season */}
      {mode==='period' ? (
        <View style={styles.segmentRow}>
          <Chip label="Minggu Ini" active={period==='week'} onPress={()=>setPeriod('week')} icon="calendar-outline" />
          <Chip label="Bulan Ini"  active={period==='month'} onPress={()=>setPeriod('month')} icon="calendar-number-outline" />
        </View>
      ) : (
        <View style={styles.seasonCtrl}>
          <Text style={{ color:COLORS.text, fontWeight:'800', marginBottom: 8 }}>Filter Musim</Text>
          <View style={styles.rowWrap}>
            {seasonList.map(sv=>(
              <TouchableOpacity
                key={sv.season_id}
                onPress={()=>setSeasonId(sv.season_id)}
                style={[styles.seasonChip, seasonId===sv.season_id && styles.seasonChipActive]}
                activeOpacity={0.85}
              >
                <Ionicons name="leaf-outline" size={14} color={seasonId===sv.season_id?COLORS.accent:COLORS.muted} />
                <Text style={[styles.seasonChipText, seasonId===sv.season_id && { color: COLORS.accent }]}>
                  {sv.name || `${sv.start_date}→${sv.end_date || '…'}`}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      )}

      {/* Search & Sort */}
      <View style={[styles.searchRow, isTiny && styles.searchRowTiny]}>
        <View style={styles.searchBox}>
          <Ionicons name="search" size={18} color={COLORS.muted} />
          <TextInput
            placeholder="Cari nama item, catatan, dll…"
            placeholderTextColor={COLORS.muted}
            value={query}
            onChangeText={setQuery}
            style={styles.searchInput}
            returnKeyType="search"
            autoCapitalize="none"
            autoCorrect={false}
            blurOnSubmit={false}
          />
          {!!query && (
            <TouchableOpacity onPress={()=>setQuery('')} activeOpacity={0.85}>
              <Ionicons name="close-circle" size={18} color={COLORS.muted} />
            </TouchableOpacity>
          )}
        </View>

        <TouchableOpacity
          style={[styles.sortBtn, isTiny && styles.sortBtnTiny]}
          onPress={()=> setSortIdx((i)=> (i+1)%SORTS.length)}
          activeOpacity={0.85}
        >
          <Ionicons name="swap-vertical-outline" size={18} color={COLORS.accent} />
          <Text style={styles.sortText}>{SORTS[sortIdx]}</Text>
        </TouchableOpacity>
      </View>

      <View style={styles.searchMetaRow}>
        <Text style={styles.searchHint} numberOfLines={1}>
          {queryNorm ? `Mencari: "${query}"` : 'Ketik bebas untuk cari nama item, kategori, subkategori, atau catatan'}
        </Text>
        <View style={styles.searchCountPill}>
          <Text style={styles.searchCountText}>{displayItems.length} item</Text>
        </View>
      </View>

      {/* Category filter */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingHorizontal: R.pad, columnGap:8 }} style={{ marginTop:6 }}>
        <Chip label="Semua" active={!category} onPress={()=>setCategory(null)} />
        {categoryOptions.map(c=> <Chip key={c} label={c} active={category===c} onPress={()=>setCategory(c)} /> )}
      </ScrollView>

      <View style={[styles.bulkCard, isTiny && styles.bulkCardTiny]}>
        <View style={{ flex: 1 }}>
          <Text style={styles.bulkTitle}>Split Massal Kategori</Text>
          <Text style={styles.bulkSub} numberOfLines={2}>
            {bulkCategoryValue
              ? `${bulkCategoryValue} • ${bulkCandidates.length} expense source • ${fmtIDR(bulkTotal)}`
              : 'Buka modal lalu pilih kategori target untuk alokasi massal.'}
          </Text>
        </View>
        <TouchableOpacity
          style={[styles.bulkBtn, isTiny && styles.bulkBtnTiny, !categoryOptions.length && styles.bulkBtnDisabled]}
          onPress={openBulkModal}
          activeOpacity={0.88}
        >
          <Ionicons name="layers-outline" size={15} color={!categoryOptions.length ? COLORS.muted : COLORS.accent} />
          <Text style={[styles.bulkBtnText, !categoryOptions.length && styles.bulkBtnTextDisabled]}>
            Split Massal
          </Text>
        </TouchableOpacity>
      </View>

      {/* Summary */}
      {!queryNorm ? (
      <View style={styles.summaryCard}>
        <View style={[styles.summaryHead, isNarrow && styles.summaryHeadNarrow]}>
          <View style={{ flexDirection:'row', alignItems:'center', columnGap:8 }}>
            <View style={styles.iconPill}>
              <Ionicons name={mode==='season' ? "leaf-outline" : "stats-chart-outline"} size={16} color={COLORS.accent} />
            </View>
            <View>
              <Text style={styles.summaryTitle}>{mode==='season' ? 'Ringkasan Musim' : 'Ringkasan'}</Text>
              <Text style={styles.summaryRange}>{stats.label}</Text>
            </View>
          </View>

          <View style={[styles.summaryTotalBlock, isNarrow && styles.summaryTotalBlockNarrow]}>
            <Text style={styles.summaryBig} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.78}>{fmtIDR(stats.total)}</Text>
            <Text style={styles.summarySub} numberOfLines={isNarrow ? 2 : 1}>{stats.count} transaksi • {stats.displayCount} item list • {fmtIDR(stats.avgPerDay)}/hari</Text>
          </View>
        </View>

        {!!topSummary?.view?.length && (
          <View style={styles.summarySectionHead}>
            <Text style={styles.summarySectionTitle}>Kategori Tertinggi</Text>
            <Text style={styles.summarySectionCaption}>Distribusi biaya per kategori</Text>
          </View>
        )}

        {!!topSummary?.view?.length && (
          <View style={styles.paretoRow}>
            <Ionicons name="analytics-outline" size={16} color={COLORS.muted} />
            <Text style={styles.paretoText}>
              Kategori Top 3 menyumbang <Text style={{ fontWeight:'900', color: COLORS.text }}>{topSummary.top3Pct}%</Text> •
              Top 5 menyumbang <Text style={{ fontWeight:'900', color: COLORS.text }}>{topSummary.top5Pct}%</Text>
            </Text>
          </View>
        )}

        {!!topSummary?.view?.length && (
          <View style={styles.summaryCategoryCard}>
            {topSummary.view.map((it, idx)=> {
              const total = Number(topSummary.total || 0);
              const v = Number(it.total || 0);
              const p = total > 0 ? Math.max(0, Math.min(1, v / total)) : 0;
              const pct = total > 0 ? Math.round((v / total) * 100) : 0;
              const isTopRank = idx < 3 && !it._others;
              const categoryKeyNorm = normalizeKey(it?.key || '(tanpa kategori)');
              const topSubs = !it._others ? (categorySubTopByKey[categoryKeyNorm] || []) : [];

              return (
                <View key={`${it.key ?? 'none'}-${idx}`} style={styles.sumRowNew}>
                  <View style={{ flex:1, paddingRight: 10 }}>
                    <View style={styles.sumRowTop}>
                      <View style={styles.sumKeyGroup}>
                        {isTopRank && (
                          <View style={styles.topRankPill}>
                            <Text style={styles.topRankText}>Top {idx + 1}</Text>
                          </View>
                        )}
                        <Text style={styles.sumKey} numberOfLines={1}>
                          {asTitle(it.key || (category ? '(tanpa subkategori)' : '(tanpa kategori)'))}
                        </Text>
                      </View>
                      <Text style={styles.sumAmt}>{fmtIDR(v)}</Text>
                    </View>
                    <View style={styles.progressTrackSoft}>
                      <View style={[styles.progressFill, { width: `${Math.round(p*100)}%` }]} />
                    </View>
                    <Text style={styles.sumSubText}>
                      Porsi {pct}%{it._others ? ' • gabungan kategori kecil' : ''}
                    </Text>
                    {!it._others && (
                      <Text style={styles.sumSubTopText} numberOfLines={2}>
                        Top sub: {topSubs.length ? topSubs.join(' • ') : 'belum ada subkategori'}
                      </Text>
                    )}
                  </View>
                </View>
              );
            })}
          </View>
        )}
      </View>
      ) : (
        <View style={styles.searchActiveCard}>
          <Ionicons name="sparkles-outline" size={16} color={COLORS.accent} />
          <Text style={styles.searchActiveText}>
            Menampilkan <Text style={{ fontWeight: '900' }}>{displayItems.length}</Text> hasil untuk "{query}"
          </Text>
        </View>
      )}

      {/* Pupuk Focus */}
      {!queryNorm && (!category || category === 'pupuk') && Number(pupukFocus?.total || 0) > 0 && (
        <View style={styles.pupukCard}>
          <View style={styles.pupukGlowA} />
          <View style={styles.pupukGlowB} />
          <View style={[styles.pupukHead, isTiny && styles.pupukHeadTiny]}>
            <View style={styles.pupukTitleRow}>
              <View style={styles.pupukIconPill}>
                <Ionicons name="nutrition-outline" size={17} color="#68723d" />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.pupukTitle}>Kategori Pupuk</Text>
                <Text style={styles.pupukSub}>Biaya nutrisi tanaman yang sudah tercatat musim ini</Text>
                <View style={styles.pupukBadgeRow}>
                  <View style={styles.pupukAuditBadge}>
                    <Ionicons name="shield-checkmark-outline" size={12} color="#52612f" />
                    <Text style={styles.pupukAuditText}>Anti-double</Text>
                  </View>
                  <View style={styles.pupukAuditBadge}>
                    <Ionicons name="layers-outline" size={12} color="#52612f" />
                    <Text style={styles.pupukAuditText}>Subkategori rapi</Text>
                  </View>
                </View>
              </View>
            </View>
            {!category && (
              <TouchableOpacity
                activeOpacity={0.82}
                style={styles.pupukFilterBtn}
                onPress={() => setCategory('pupuk')}
              >
                <Text style={styles.pupukFilterText}>Lihat Pupuk</Text>
                <Ionicons name="arrow-forward" size={13} color="#5c512e" />
              </TouchableOpacity>
            )}
          </View>

          <View style={styles.pupukMetricGrid}>
            <View style={styles.pupukMetricMain}>
              <Text style={styles.pupukMetricLabel}>Total Pupuk</Text>
              <Text style={styles.pupukMetricValue}>{fmtIDR(pupukFocus.total)}</Text>
            </View>
            <View style={styles.pupukMetricMini}>
              <Text style={styles.pupukMetricLabel}>Porsi</Text>
              <Text style={styles.pupukMetricSmall}>{pupukFocus.pct}%</Text>
            </View>
            <View style={styles.pupukMetricMini}>
              <Text style={styles.pupukMetricLabel}>Jenis</Text>
              <Text style={styles.pupukMetricSmall}>{pupukFocus.typeCount}</Text>
            </View>
          </View>

          {!!pupukFocus?.dominant && (
            <View style={styles.pupukInsightStrip}>
              <View style={styles.pupukInsightDot} />
              <Text style={styles.pupukInsightText} numberOfLines={2}>
                Terbesar saat ini <Text style={styles.pupukInsightStrong}>{pupukFocus.dominant.label}</Text> dengan porsi <Text style={styles.pupukInsightStrong}>{pupukFocus.dominant.pct}%</Text> dari total pupuk.
              </Text>
            </View>
          )}

          {!!pupukFocus?.businessGroups?.length && (
            <View style={styles.pupukBusinessBox}>
              <View style={styles.pupukBusinessHead}>
                <View>
                  <Text style={styles.pupukBusinessTitle}>Breakdown Bisnis</Text>
                  <Text style={styles.pupukBusinessSub}>Dikelompokkan sesuai fungsi biaya</Text>
                </View>
                <View style={styles.pupukBusinessPill}>
                  <Text style={styles.pupukBusinessPillText}>Owner View</Text>
                </View>
              </View>

              {pupukFocus.businessGroups.map((group) => {
                const barWidth = `${Math.max(4, Math.min(100, group.pct))}%`;
                return (
                  <View key={`pupuk-business-${group.key}`} style={styles.pupukBusinessRow}>
                    <View style={[styles.pupukBusinessIcon, { backgroundColor: group.soft, borderColor: group.border }]}>
                      <Ionicons name={group.icon} size={15} color={group.color} />
                    </View>
                    <View style={styles.pupukBusinessCopy}>
                      <View style={styles.pupukBusinessLine}>
                        <Text style={styles.pupukBusinessName} numberOfLines={1}>{group.title}</Text>
                        <Text style={styles.pupukBusinessAmount}>{fmtIDR(group.total)}</Text>
                      </View>
                      <Text style={styles.pupukBusinessDesc} numberOfLines={1}>{group.subtitle}</Text>
                      <View style={styles.pupukBusinessTrack}>
                        <View style={[styles.pupukBusinessFill, { width: barWidth, backgroundColor: group.color }]} />
                      </View>
                    </View>
                    <Text style={styles.pupukBusinessPct}>{group.pct}%</Text>
                  </View>
                );
              })}
            </View>
          )}

          <View style={styles.pupukTopBox}>
            <View style={styles.pupukTopHead}>
              <Text style={styles.pupukTopTitle}>Top 3 jenis pupuk</Text>
              <Text style={styles.pupukTopCaption}>berdasarkan nominal</Text>
            </View>
            {pupukFocus.topItems.length ? (
              pupukFocus.topItems.map((it, idx) => (
                <View key={`pupuk-top-${it.key}-${idx}`} style={styles.pupukTopRow}>
                  <View style={styles.pupukRank}>
                    <Text style={styles.pupukRankText}>{idx + 1}</Text>
                  </View>
                  <View style={{ flex: 1, paddingRight: 8 }}>
                    <Text style={styles.pupukItemName} numberOfLines={1}>{it.label || asPupukTypeLabel(it.key)}</Text>
                    <View style={styles.pupukBarTrack}>
                      <View style={[styles.pupukBarFill, { width: `${Math.max(4, Math.min(100, it.pct))}%` }]} />
                    </View>
                  </View>
                  <View style={styles.pupukAmountWrap}>
                    <Text style={styles.pupukAmount}>{fmtIDR(it.total)}</Text>
                    <Text style={styles.pupukPct}>{it.pct}%</Text>
                  </View>
                </View>
              ))
            ) : (
              <Text style={styles.pupukEmptyText}>Total pupuk sudah ada, detail item belum terbaca di list saat ini.</Text>
            )}
          </View>
        </View>
      )}

      {/* Pesticide Detail */}
      {!queryNorm && (!category || category==='pestisida') && !!pestTop?.view?.length && (
        <View style={styles.sectionStack}>
        <View style={styles.sectionDivider} />
        <View style={styles.pestCard}>
          <View style={styles.pestHead}>
            <View style={{ flexDirection:'row', alignItems:'center', columnGap:8 }}>
              <View style={[styles.iconPill, { borderColor: COLORS.sky, backgroundColor: COLORS.skySoft }]}>
                <Ionicons name="flask-outline" size={16} color={COLORS.sky} />
              </View>
              <View>
                <Text style={styles.pestTitle}>Rincian Pestisida</Text>
                <Text style={styles.pestSub}>Top jenis + gabungan lainnya • total {fmtIDR(pestTop.total)}</Text>
              </View>
            </View>
          </View>

          {pestTop.view.map((row, idx)=> {
            const v = Number(row.total || 0);
            const p = pestTop.total > 0 ? Math.max(0, Math.min(1, v / pestTop.total)) : 0;
            const pct = pestTop.pct(v);

            return (
              <View key={`${row.key}-${idx}`} style={styles.pestRowNew}>
                <View style={{ flex:1, paddingRight: 10 }}>
                  <View style={styles.sumRowTop}>
                    <Text style={styles.pestKind} numberOfLines={1}>{row.key}</Text>
                    <Text style={styles.sumAmt}>{fmtIDR(v)}</Text>
                  </View>
                  <View style={[styles.progressTrackSoft, { backgroundColor:'rgba(14,165,233,0.12)' }]}>
                    <View style={[styles.progressFillSky, { width: `${Math.round(p*100)}%` }]} />
                  </View>
                  <Text style={styles.sumSubText}>
                    Porsi {pct}%{row._others ? ' • gabungan jenis lain' : ''}
                  </Text>
                </View>
              </View>
            );
          })}
        </View>
        </View>
      )}
    </View>
  );

  const renderItem = ({ item }) => {
    const qty = item.quantity ? `${Number(item.quantity)} ${item.unit || ''}`.trim() : '';
    const unit = item.unit_price ? `× ${fmtIDR(item.unit_price)}` : '';
    const fieldLabel = item.field_name || null;
    const src = String(item?.source_type || '').toLowerCase();
    const isAllocationGroup = src === 'field_split_group';
    const allocationFields = Array.isArray(item?.allocation_fields) ? item.allocation_fields : [];
    const allocationCount = Number(item?.allocation_count || allocationFields.length || 0);
    const allocationPreview = allocationFields.slice(0, 3);
    const allocationMore = Math.max(0, allocationCount - allocationPreview.length);
    const allocationKey = item?.group_key || `field_split_group:${item?.expense_id || ''}:${item?.expense_date || ''}:${item?.item_name || ''}`;
    const allocationRows = Array.isArray(item?.allocation_rows) ? item.allocation_rows : [];
    const allocationExpanded = !!expandedAllocations[allocationKey];
    const allocationDetailRows = allocationExpanded ? allocationRows : [];
    const canSplitExistingExpense =
      Number(item?.total || 0) > 0 &&
      Number(item?.expense_id || 0) > 0 &&
      (src === '' || src === 'expense');
    const sourceLabel = isAllocationGroup
      ? `Alokasi ${allocationCount} lahan`
      : src === 'field_split'
      ? 'Alokasi Lahan'
      : 'Expense Umum';
    const categoryLabel = asTitle(item?.category || '-');
    const subcategoryLabel = item?.subcategory ? asTitle(item.subcategory) : null;
    const itemMetaParts = [
      fmtDate(item.expense_date),
      isAllocationGroup ? `${allocationCount} lahan` : fieldLabel,
    ].filter(Boolean);
    const detailLine = isAllocationGroup ? '' : [qty, unit].filter(Boolean).join(' ');

    return (
      <View style={[styles.card, isNarrow && styles.cardMobile]}>
        <View style={styles.cardAccent} />
        <View style={[styles.cardHeadRow, isTiny && styles.cardHeadRowTiny]}>
          <View style={styles.itemSourceWrap}>
            <View style={styles.itemIconPill}>
              <Ionicons name={catIcon(item.category)} size={15} color={COLORS.accent} />
            </View>
            <View style={styles.sourceBadge}>
              <Text style={styles.sourceBadgeText}>{sourceLabel}</Text>
            </View>
          </View>
          <View style={[styles.amountBox, isTiny && styles.amountBoxTiny]}>
            <Text style={styles.amountLabel}>Total</Text>
            <Text style={styles.amount} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.78}>{fmtIDR(item.total)}</Text>
          </View>
        </View>

        <View style={styles.itemBody}>
          <Text style={styles.itemTitle} numberOfLines={isTiny ? 2 : 1}>
            {item.item_name || (item.category + (item.subcategory ? ` / ${item.subcategory}` : ''))}
          </Text>
          <Text style={styles.itemMeta} numberOfLines={isTiny ? 2 : 1}>{itemMetaParts.join(' • ')}</Text>
          <Text style={styles.categoryLine} numberOfLines={isTiny ? 2 : 1}>
            {categoryLabel}{subcategoryLabel ? ` • ${subcategoryLabel}` : ''}
          </Text>
        </View>

        {!!detailLine && <Text style={styles.qtyPrice}>{detailLine}</Text>}
        {!!item.notes && <Text style={styles.itemNote} numberOfLines={1}>{item.notes}</Text>}

        {isAllocationGroup && (
          <View style={styles.allocationSummaryBox}>
            <View style={styles.allocationSummaryHead}>
              <Ionicons name="git-network-outline" size={14} color={COLORS.accent} />
              <Text style={styles.allocationSummaryTitle}>Lahan teralokasi</Text>
            </View>
            <View style={styles.allocationFieldsRow}>
              {allocationPreview.map((name) => (
                <View key={`alloc-field-${item.expense_id}-${name}`} style={styles.allocationFieldPill}>
                  <Text style={styles.allocationFieldText} numberOfLines={1}>{name}</Text>
                </View>
              ))}
              {allocationMore > 0 && (
                <View style={styles.allocationFieldPillMore}>
                  <Text style={styles.allocationFieldMoreText}>+{allocationMore} lahan</Text>
                </View>
              )}
            </View>
            {allocationRows.length > 0 && (
              <TouchableOpacity
                activeOpacity={0.82}
                style={styles.allocationDetailToggle}
                onPress={() => setExpandedAllocations((prev) => ({
                  ...prev,
                  [allocationKey]: !prev[allocationKey],
                }))}
              >
                <View>
                  <Text style={styles.allocationDetailToggleText}>
                    {allocationExpanded ? 'Ringkas detail' : 'Lihat detail pembagian'}
                  </Text>
                  <Text style={styles.allocationDetailToggleSub}>
                    {allocationCount} lahan • total {fmtIDR(item.total)}
                  </Text>
                </View>
                <Ionicons
                  name={allocationExpanded ? 'chevron-up' : 'chevron-down'}
                  size={16}
                  color={COLORS.accent}
                />
              </TouchableOpacity>
            )}
            {allocationDetailRows.length > 0 && (
              <View style={styles.allocationDetailList}>
                {allocationDetailRows.map((row, idx) => {
                  const rowField = row?.field_name || `Lahan ${idx + 1}`;
                  const rowQty = row?.quantity ? `${Number(row.quantity)} ${row.unit || ''}`.trim() : '';
                  const rowUnit = row?.unit_price ? `× ${fmtIDR(row.unit_price)}` : '';
                  const rowMeta = [rowQty, rowUnit].filter(Boolean).join(' ');
                  return (
                    <View
                      key={`alloc-row-${allocationKey}-${row?.field_expense_id || row?.field_id || idx}`}
                      style={styles.allocationDetailRow}
                    >
                      <View style={styles.allocationDetailCopy}>
                        <Text style={styles.allocationDetailName} numberOfLines={1}>{rowField}</Text>
                        {!!rowMeta && <Text style={styles.allocationDetailMeta} numberOfLines={1}>{rowMeta}</Text>}
                      </View>
                      <Text style={styles.allocationDetailAmt}>{fmtIDR(row?.total || 0)}</Text>
                    </View>
                  );
                })}
              </View>
            )}
          </View>
        )}

        {!isAllocationGroup && (
          <View style={styles.badgesRow}>
            <View style={styles.badge}>
              <Ionicons name="leaf-outline" size={12} color={COLORS.accent} />
              <Text style={styles.badgeText}>{item.season_name || '—'}</Text>
            </View>
          </View>
        )}

        {canSplitExistingExpense && (
          <View style={styles.actionRow}>
            <TouchableOpacity
              style={styles.splitBtn}
              onPress={() => openSplitModal(item)}
              activeOpacity={0.88}
            >
              <Ionicons name="git-merge-outline" size={14} color={COLORS.accent} />
              <Text style={styles.splitBtnText}>Split Alokasi Biaya</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
    );
  };

  return (
    <View style={styles.screen}>
      <View pointerEvents="none" style={styles.screenGlowTop} />
      {/* Modal create season (dipisah -> input gak kedip) */}
      <CreateSeasonModal
        visible={createSeasonOpen}
        onClose={() => setCreateSeasonOpen(false)}
        onSubmit={submitCreateSeason}
        colors={COLORS}
        radius={R.radius}
        pad={R.pad}
        accent={COLORS.accent}
        accentSoft={COLORS.accentSoft}
        sky={COLORS.sky}
        skySoft={COLORS.skySoft}
      />

      <Modal visible={splitOpen} transparent animationType="fade" onRequestClose={closeSplitModal}>
        <View style={styles.modalBackdrop}>
          <View style={[contentWidthStyle, styles.modalCard, isNarrow && styles.modalCardNarrow]}>
            <View style={styles.modalHead}>
              <Text style={styles.modalTitle}>Split Alokasi Biaya</Text>
              <TouchableOpacity onPress={closeSplitModal} style={styles.modalClose} activeOpacity={0.85}>
                <Ionicons name="close" size={16} color={COLORS.muted} />
              </TouchableOpacity>
            </View>

            <Text style={styles.modalSub} numberOfLines={2}>
              {splitExpense?.item_name || splitExpense?.category || '-'} • {fmtIDR(splitExpense?.total || 0)}
            </Text>

            <ScrollView
              style={styles.modalBodyScroll}
              contentContainerStyle={styles.modalBodyContent}
              showsVerticalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
            >
              <View style={styles.modalStatsRow}>
                <View style={styles.modalStatItem}>
                  <Text style={styles.modalStatLabel}>Terpilih</Text>
                  <Text style={styles.modalStatValue}>{splitStats.selectedCount} lahan</Text>
                </View>
                <View style={styles.modalStatDivider} />
                <View style={styles.modalStatItem}>
                  <Text style={styles.modalStatLabel}>Total Rante</Text>
                  <Text style={styles.modalStatValue}>{Number(splitStats.selectedArea || 0).toFixed(2)}</Text>
                </View>
              </View>

              <View style={styles.modalSwitchRow}>
                <Chip label="Otomatis (Luas)" active={splitMethod === 'area'} onPress={() => setSplitMethod('area')} />
                <Chip label="Rata" active={splitMethod === 'equal'} onPress={() => setSplitMethod('equal')} />
              </View>

              <View style={styles.modalListWrap}>
                <View style={styles.modalToolsRow}>
                  <View style={styles.modalSearchWrap}>
                    <Ionicons name="search-outline" size={16} color={COLORS.muted} />
                    <TextInput
                      value={splitQuery}
                      onChangeText={setSplitQuery}
                      placeholder="Cari lahan..."
                      placeholderTextColor={COLORS.muted}
                      style={styles.modalSearchInput}
                    />
                  </View>
                  <View style={styles.modalToolsActionRow}>
                    <TouchableOpacity onPress={selectAllSplitFields} activeOpacity={0.85} style={styles.modalToolBtn}>
                      <Text style={styles.modalToolBtnText}>Pilih Semua</Text>
                    </TouchableOpacity>
                    <TouchableOpacity onPress={clearSplitFields} activeOpacity={0.85} style={styles.modalToolBtnGhost}>
                      <Text style={styles.modalToolBtnGhostText}>Reset</Text>
                    </TouchableOpacity>
                  </View>
                </View>

                {splitLoading ? (
                  <View style={styles.modalLoadingBox}>
                    <ActivityIndicator />
                    <Text style={styles.modalLoadingText}>Memuat lahan...</Text>
                  </View>
                ) : splitFilteredFields.length === 0 ? (
                  <View style={styles.modalEmptyBox}>
                    <Text style={styles.modalEmptyText}>Lahan tidak ditemukan.</Text>
                  </View>
                ) : (
                  <ScrollView showsVerticalScrollIndicator={false}>
                    {splitFilteredFields.map((f) => {
                      const checked = splitSelected.includes(f.id);
                      return (
                        <TouchableOpacity
                          key={f.id}
                          style={[styles.modalFieldRow, checked && styles.modalFieldRowActive]}
                          onPress={() => toggleSplitField(f.id)}
                          activeOpacity={0.88}
                        >
                          <View style={{ flex: 1 }}>
                            <Text style={styles.modalFieldName}>{f.name}</Text>
                            <Text style={styles.modalFieldMeta}>
                              {f.area_rante != null ? `${f.area_rante} rante` : 'rante belum diisi'}
                            </Text>
                          </View>
                          <Ionicons
                            name={checked ? 'checkmark-circle' : 'ellipse-outline'}
                            size={20}
                            color={checked ? COLORS.accent : COLORS.muted}
                          />
                        </TouchableOpacity>
                      );
                    })}
                  </ScrollView>
                )}
              </View>

              {splitPreview.rows.length > 0 && (
                <View style={styles.modalPreviewBox}>
                  <Text style={styles.modalPreviewTitle}>Preview Pembagian</Text>
                  <View style={styles.modalPreviewHeadGrid}>
                    <View style={styles.modalPreviewHeadItem}>
                      <Text style={styles.modalPreviewHeadLabel}>Total expense</Text>
                      <Text style={styles.modalPreviewHeadValue}>{fmtIDR(splitExpense?.total || 0)}</Text>
                    </View>
                    <View style={styles.modalPreviewHeadItem}>
                      <Text style={styles.modalPreviewHeadLabel}>Jumlah lahan</Text>
                      <Text style={styles.modalPreviewHeadValue}>{splitStats.selectedCount}</Text>
                    </View>
                    {splitPreview.methodUsed === 'area' ? (
                      <>
                        <View style={styles.modalPreviewHeadItem}>
                          <Text style={styles.modalPreviewHeadLabel}>Total rante</Text>
                          <Text style={styles.modalPreviewHeadValue}>{fmtRante(splitPreview.denominator)}</Text>
                        </View>
                        <View style={styles.modalPreviewHeadItem}>
                          <Text style={styles.modalPreviewHeadLabel}>Biaya per rante</Text>
                          <Text style={styles.modalPreviewHeadValue}>{fmtIDR(splitPreview.unitCost)}</Text>
                        </View>
                      </>
                    ) : (
                      <View style={styles.modalPreviewHeadItemWide}>
                        <Text style={styles.modalPreviewHeadLabel}>Biaya per lahan (rata)</Text>
                        <Text style={styles.modalPreviewHeadValue}>{fmtIDR(splitPreview.unitCost)}</Text>
                      </View>
                    )}
                  </View>
                  {splitPreview.rows.map((it) => (
                    <View key={it.id} style={styles.modalPreviewRow}>
                      <View style={{ flex: 1, paddingRight: 8 }}>
                        <Text style={styles.modalPreviewName} numberOfLines={1}>{it.name}</Text>
                        {splitPreview.methodUsed === 'area' ? (
                          <Text style={styles.modalPreviewMeta}>{fmtRante(it.area)} / {fmtRante(splitPreview.denominator)} rante</Text>
                        ) : (
                          <Text style={styles.modalPreviewMeta}>1 / {splitStats.selectedCount} bagian</Text>
                        )}
                      </View>
                      <Text style={styles.modalPreviewAmt}>{fmtIDR(it.amount)}</Text>
                    </View>
                  ))}
                  <View style={styles.modalPreviewTotalRow}>
                    <Text style={styles.modalPreviewTotalLabel}>Total hasil pembagian (preview)</Text>
                    <Text style={styles.modalPreviewTotalAmt}>{fmtIDR(splitPreview.totalAllocated)}</Text>
                  </View>
                  <Text style={styles.modalPreviewAssist}>
                    Harus sama dengan total expense agar tidak double hitung.
                  </Text>
                  <View style={styles.modalPreviewTotalRow}>
                    <Text style={styles.modalPreviewTotalLabel}>Selisih vs total expense</Text>
                    <Text style={styles.modalPreviewDeltaAmt}>
                      {fmtSignedIDR(splitPreview.totalAllocated - Number(splitExpense?.total || 0))}
                    </Text>
                  </View>
                  {!!splitPreview.invalidAreaCount && splitMethod === 'area' && (
                    <Text style={styles.modalPreviewWarn}>
                      {splitPreview.invalidAreaCount} lahan belum punya rante valid, pembagian sementara rata.
                    </Text>
                  )}
                </View>
              )}
            </ScrollView>

            <View style={styles.modalActionRow}>
              <TouchableOpacity style={styles.modalBtnGhost} onPress={closeSplitModal} activeOpacity={0.85}>
                <Text style={styles.modalBtnGhostText}>Batal</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalBtnPrimary, splitSubmitting && { opacity: 0.75 }]}
                onPress={submitSplit}
                activeOpacity={0.9}
                disabled={splitSubmitting}
              >
                {splitSubmitting ? <ActivityIndicator color="#fff" /> : <Text style={styles.modalBtnPrimaryText}>Split Sekarang</Text>}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Modal visible={bulkOpen} transparent animationType="fade" onRequestClose={closeBulkModal}>
        <View style={styles.modalBackdrop}>
          <View style={[contentWidthStyle, styles.modalCard, isNarrow && styles.modalCardNarrow]}>
            <View style={styles.modalHead}>
              <Text style={styles.modalTitle}>Split Massal Kategori</Text>
              <TouchableOpacity onPress={closeBulkModal} style={styles.modalClose} activeOpacity={0.85}>
                <Ionicons name="close" size={16} color={COLORS.muted} />
              </TouchableOpacity>
            </View>

            <Text style={styles.modalSub} numberOfLines={2}>
              {bulkCategoryValue || '-'} • {bulkCandidates.length} expense • {fmtIDR(bulkTotal)}
            </Text>

            <ScrollView
              style={styles.modalBodyScroll}
              contentContainerStyle={styles.modalBodyContent}
              showsVerticalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
            >
              <Text style={styles.modalSectionLabel}>Kategori Target</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.modalCategoryRow}>
                {categoryOptions.map((c) => {
                  const active = bulkCategoryValue === c;
                  return (
                    <TouchableOpacity
                      key={`bulk-cat-${c}`}
                      style={[styles.modalCategoryChip, active && styles.modalCategoryChipActive]}
                      activeOpacity={0.88}
                      onPress={() => setBulkCategory(c)}
                    >
                      <Text style={[styles.modalCategoryChipText, active && styles.modalCategoryChipTextActive]}>{c}</Text>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>

              <View style={styles.modalStatsRow}>
                <View style={styles.modalStatItem}>
                  <Text style={styles.modalStatLabel}>Terpilih</Text>
                  <Text style={styles.modalStatValue}>{splitStats.selectedCount} lahan</Text>
                </View>
                <View style={styles.modalStatDivider} />
                <View style={styles.modalStatItem}>
                  <Text style={styles.modalStatLabel}>Total Rante</Text>
                  <Text style={styles.modalStatValue}>{Number(splitStats.selectedArea || 0).toFixed(2)}</Text>
                </View>
              </View>

              <View style={styles.modalSwitchRow}>
                <Chip label="Otomatis (Luas)" active={splitMethod === 'area'} onPress={() => setSplitMethod('area')} />
                <Chip label="Rata" active={splitMethod === 'equal'} onPress={() => setSplitMethod('equal')} />
              </View>

              <View style={styles.modalListWrap}>
                <View style={styles.modalToolsRow}>
                  <View style={styles.modalSearchWrap}>
                    <Ionicons name="search-outline" size={16} color={COLORS.muted} />
                    <TextInput
                      value={splitQuery}
                      onChangeText={setSplitQuery}
                      placeholder="Cari lahan..."
                      placeholderTextColor={COLORS.muted}
                      style={styles.modalSearchInput}
                    />
                  </View>
                  <View style={styles.modalToolsActionRow}>
                    <TouchableOpacity onPress={selectAllSplitFields} activeOpacity={0.85} style={styles.modalToolBtn}>
                      <Text style={styles.modalToolBtnText}>Pilih Semua</Text>
                    </TouchableOpacity>
                    <TouchableOpacity onPress={clearSplitFields} activeOpacity={0.85} style={styles.modalToolBtnGhost}>
                      <Text style={styles.modalToolBtnGhostText}>Reset</Text>
                    </TouchableOpacity>
                  </View>
                </View>

                {splitLoading ? (
                  <View style={styles.modalLoadingBox}>
                    <ActivityIndicator />
                    <Text style={styles.modalLoadingText}>Memuat lahan...</Text>
                  </View>
                ) : splitFilteredFields.length === 0 ? (
                  <View style={styles.modalEmptyBox}>
                    <Text style={styles.modalEmptyText}>Lahan tidak ditemukan.</Text>
                  </View>
                ) : (
                  <ScrollView showsVerticalScrollIndicator={false}>
                    {splitFilteredFields.map((f) => {
                      const checked = splitSelected.includes(f.id);
                      return (
                        <TouchableOpacity
                          key={f.id}
                          style={[styles.modalFieldRow, checked && styles.modalFieldRowActive]}
                          onPress={() => toggleSplitField(f.id)}
                          activeOpacity={0.88}
                        >
                          <View style={{ flex: 1 }}>
                            <Text style={styles.modalFieldName}>{f.name}</Text>
                            <Text style={styles.modalFieldMeta}>
                              {f.area_rante != null ? `${f.area_rante} rante` : 'rante belum diisi'}
                            </Text>
                          </View>
                          <Ionicons
                            name={checked ? 'checkmark-circle' : 'ellipse-outline'}
                            size={20}
                            color={checked ? COLORS.accent : COLORS.muted}
                          />
                        </TouchableOpacity>
                      );
                    })}
                  </ScrollView>
                )}
              </View>

              {bulkPreview.rows.length > 0 && (
                <View style={styles.modalPreviewBox}>
                  <Text style={styles.modalPreviewTitle}>Preview Pembagian Massal</Text>
                  <View style={styles.modalPreviewHeadGrid}>
                    <View style={styles.modalPreviewHeadItem}>
                      <Text style={styles.modalPreviewHeadLabel}>Total sumber kategori</Text>
                      <Text style={styles.modalPreviewHeadValue}>{fmtIDR(bulkTotal)}</Text>
                    </View>
                    <View style={styles.modalPreviewHeadItem}>
                      <Text style={styles.modalPreviewHeadLabel}>Jumlah lahan</Text>
                      <Text style={styles.modalPreviewHeadValue}>{splitStats.selectedCount}</Text>
                    </View>
                    {bulkPreview.methodUsed === 'area' ? (
                      <>
                        <View style={styles.modalPreviewHeadItem}>
                          <Text style={styles.modalPreviewHeadLabel}>Total rante</Text>
                          <Text style={styles.modalPreviewHeadValue}>{fmtRante(bulkPreview.denominator)}</Text>
                        </View>
                        <View style={styles.modalPreviewHeadItem}>
                          <Text style={styles.modalPreviewHeadLabel}>Biaya per rante</Text>
                          <Text style={styles.modalPreviewHeadValue}>{fmtIDR(bulkPreview.unitCost)}</Text>
                        </View>
                      </>
                    ) : (
                      <View style={styles.modalPreviewHeadItemWide}>
                        <Text style={styles.modalPreviewHeadLabel}>Biaya per lahan (rata)</Text>
                        <Text style={styles.modalPreviewHeadValue}>{fmtIDR(bulkPreview.unitCost)}</Text>
                      </View>
                    )}
                  </View>
                  {bulkPreview.rows.map((it) => (
                    <View key={`bulk-prev-${it.id}`} style={styles.modalPreviewRow}>
                      <View style={{ flex: 1, paddingRight: 8 }}>
                        <Text style={styles.modalPreviewName} numberOfLines={1}>{it.name}</Text>
                        {bulkPreview.methodUsed === 'area' ? (
                          <Text style={styles.modalPreviewMeta}>{fmtRante(it.area)} / {fmtRante(bulkPreview.denominator)} rante</Text>
                        ) : (
                          <Text style={styles.modalPreviewMeta}>1 / {splitStats.selectedCount} bagian</Text>
                        )}
                      </View>
                      <Text style={styles.modalPreviewAmt}>{fmtIDR(it.amount)}</Text>
                    </View>
                  ))}
                  <View style={styles.modalPreviewTotalRow}>
                    <Text style={styles.modalPreviewTotalLabel}>Total hasil pembagian (preview)</Text>
                    <Text style={styles.modalPreviewTotalAmt}>{fmtIDR(bulkPreview.totalAllocated)}</Text>
                  </View>
                  <Text style={styles.modalPreviewAssist}>
                    Harus sama dengan total sumber kategori agar tidak double hitung.
                  </Text>
                  <View style={styles.modalPreviewTotalRow}>
                    <Text style={styles.modalPreviewTotalLabel}>Selisih vs total sumber</Text>
                    <Text style={styles.modalPreviewDeltaAmt}>{fmtSignedIDR(bulkPreview.totalAllocated - bulkTotal)}</Text>
                  </View>
                  {!!bulkPreview.invalidAreaCount && splitMethod === 'area' && (
                    <Text style={styles.modalPreviewWarn}>
                      {bulkPreview.invalidAreaCount} lahan belum punya rante valid, pembagian sementara rata.
                    </Text>
                  )}
                </View>
              )}
            </ScrollView>

            <View style={styles.modalActionRow}>
              <TouchableOpacity style={styles.modalBtnGhost} onPress={closeBulkModal} activeOpacity={0.85}>
                <Text style={styles.modalBtnGhostText}>Batal</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalBtnPrimary, bulkSubmitting && { opacity: 0.75 }]}
                onPress={submitBulkSplit}
                activeOpacity={0.9}
                disabled={bulkSubmitting}
              >
                {bulkSubmitting ? <ActivityIndicator color="#fff" /> : <Text style={styles.modalBtnPrimaryText}>Split Massal</Text>}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {loading && items.length === 0 ? (
        <View style={styles.loadingWrap}>
          <ActivityIndicator size="large" />
          <Text style={{ color: COLORS.muted, marginTop: 8 }}>Memuat data…</Text>
        </View>
      ) : (
        <FlatList
          ref={listRef}
          style={[styles.list, contentWidthStyle]}
          data={displayItems}
          keyExtractor={(it)=>`${it.source_type || 'expense'}:${it.field_expense_id || it.expense_id}:${it.created_at || ''}`}
          renderItem={renderItem}
          contentContainerStyle={styles.listContent}
          ListHeaderComponent={renderHeader()}
          ListHeaderComponentStyle={{ backgroundColor: COLORS.bg }}
          ListEmptyComponent={
            <View style={styles.empty}>
              <View style={{ marginBottom: 8 }}>
                <PadiProIcon name="stage-vegetatif" size={34} color={COLORS.accent} strokeWidth={2} />
              </View>
              <Text style={{ color: COLORS.muted, textAlign:'center' }}>
                Belum ada data untuk filter/pencarian ini.
              </Text>
            </View>
          }
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={COLORS.accent} />}
          removeClippedSubviews
          contentInsetAdjustmentBehavior="automatic"
          showsVerticalScrollIndicator={false}
        />
      )}

      {/* FAB */}
      <TouchableOpacity
        style={styles.fab}
        activeOpacity={0.85}
        onPress={()=>navigation.navigate('ReceiptScan', { token })}
      >
        <Ionicons name="receipt-outline" size={24} color="#fff" />
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex:1, backgroundColor: COLORS.bg },
  screenGlowTop: {
    position: 'absolute',
    top: -70,
    right: -55,
    width: 230,
    height: 230,
    borderRadius: 999,
    backgroundColor: 'rgba(31,138,76,0.10)',
  },
  list: { width: '100%', alignSelf: 'center' },
  listContent: { paddingBottom: 110 },

  headerWrap: { paddingBottom: 8 },
  headerActions: { flexDirection: 'row', alignItems: 'center', columnGap: 8 },
  scanReceiptBtn: {
    minHeight: 44,
    paddingHorizontal: 12,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#b8e5c8',
    backgroundColor: '#ecfdf5',
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 6,
    ...padiproShadow,
  },
  scanReceiptText: { color: COLORS.accent, fontWeight: '900', fontSize: 12 },
  addBtn: {
    backgroundColor: COLORS.accent,
    borderRadius: 999,
    width: 44,
    height: 44,
    alignItems:'center',
    justifyContent:'center',
    ...padiproShadow,
  },

  banner:{ marginHorizontal:R.pad, marginTop:8, borderWidth:1, borderColor:COLORS.accent, backgroundColor:COLORS.accentSoft, borderRadius:R.radius, padding:R.pad, flexDirection:'row', alignItems:'center', flexWrap:'wrap', gap:8 },
  bannerText:{ color:COLORS.text, flex:1 },
  bannerBtn:{ backgroundColor: COLORS.accent, paddingHorizontal:12, paddingVertical:10, minHeight:44, borderRadius:999, justifyContent:'center' },
  bannerBtnText:{ color:'#fff', fontWeight:'800' },

  segmentRow: { flexDirection:'row', flexWrap:'wrap', gap:8, paddingHorizontal: R.pad, marginTop: 10 },

  chip: { borderWidth:1, borderColor: COLORS.border, borderRadius: 999, minHeight: 38, paddingVertical: 7, paddingHorizontal: 12, backgroundColor:'#fff', flexDirection:'row', alignItems:'center', ...shadow },
  chipText: { color: COLORS.text, fontWeight:'700' },

  seasonCtrl:{ borderWidth:1, borderColor: COLORS.border, borderRadius: R.radius, backgroundColor: COLORS.card, marginHorizontal: R.pad, marginTop: 8, padding: R.pad, ...shadow },
  rowWrap:{ flexDirection:'row', flexWrap:'wrap' },
  seasonChip:{ flexDirection:'row', alignItems:'center', columnGap:6, borderWidth:1, borderColor: COLORS.border, paddingVertical:6, paddingHorizontal:12, backgroundColor:'#fff', borderRadius:999, marginRight:8, marginBottom:8, ...shadow },
  seasonChipActive:{ borderColor: COLORS.accent, backgroundColor: COLORS.accentSoft },
  seasonChipText:{ color: COLORS.text, fontWeight:'700' },

  // Search/sort
  searchRow: { paddingHorizontal: R.pad, flexDirection:'row', columnGap:8, rowGap:8, marginTop: 10 },
  searchRowTiny: { flexWrap: 'wrap' },
  searchBox: { flex:1, flexDirection:'row', alignItems:'center', borderWidth:1, borderColor: '#cfe1d4', borderRadius: 999, backgroundColor:'#fff', paddingHorizontal: 12, height:46, ...shadow },
  searchInput: { flex:1, marginLeft: 8, color: COLORS.text, fontSize: 14 },
  sortBtn: { height:46, borderRadius: 999, paddingHorizontal: 12, backgroundColor:'#fff', borderWidth:1, borderColor: '#cfe1d4', flexDirection:'row', alignItems:'center', ...shadow },
  sortBtnTiny: { width: '100%', justifyContent: 'center' },
  sortText: { marginLeft:6, color: COLORS.accent, fontWeight:'700' },
  searchMetaRow: {
    marginTop: 6,
    paddingHorizontal: R.pad,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  searchHint: {
    flex: 1,
    color: COLORS.muted,
    fontSize: 12,
    paddingRight: 8,
  },
  searchCountPill: {
    borderWidth: 1,
    borderColor: '#cfe1d4',
    backgroundColor: '#f5faf6',
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  searchCountText: {
    color: '#315341',
    fontSize: 11,
    fontWeight: '800',
  },
  bulkCard: {
    marginTop: 10,
    marginHorizontal: R.pad,
    borderWidth: 1,
    borderColor: '#b8e5c8',
    backgroundColor: '#ecfdf5',
    borderRadius: R.radius,
    padding: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  bulkCardTiny: { flexDirection: 'column', alignItems: 'stretch' },
  bulkTitle: { color: COLORS.accent, fontWeight: '900', fontSize: 14 },
  bulkSub: { marginTop: 3, color: '#335541', fontWeight: '700', fontSize: 12 },
  bulkBtn: {
    borderWidth: 1,
    borderColor: '#9fdbb6',
    backgroundColor: '#e8f9ef',
    borderRadius: 999,
    paddingHorizontal: 12,
    paddingVertical: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 44,
  },
  bulkBtnTiny: { width: '100%' },
  bulkBtnDisabled: {
    borderColor: PADIPRO.border,
    backgroundColor: PADIPRO.surfaceSunken,
  },
  bulkBtnText: { marginLeft: 6, color: COLORS.accent, fontWeight: '900', fontSize: 12 },
  bulkBtnTextDisabled: { color: COLORS.muted },

  // Summary
  summaryCard:{ borderWidth:1, borderColor: COLORS.border, borderRadius: R.radius, backgroundColor: COLORS.card, marginHorizontal: R.pad, marginTop: 10, padding: R.pad, ...shadow },
  sectionStack: { marginTop: 0 },
  sectionDivider: {
    height: 1,
    marginHorizontal: R.pad,
    marginTop: 8,
    marginBottom: 2,
    backgroundColor: '#e3eee6',
  },
  searchActiveCard:{
    borderWidth:1,
    borderColor:'#cde7d0',
    borderRadius:R.radius,
    backgroundColor:'#f3fbf5',
    marginHorizontal:R.pad,
    marginTop:10,
    paddingVertical:10,
    paddingHorizontal:12,
    flexDirection:'row',
    alignItems:'center',
  },
  searchActiveText:{ marginLeft:8, color:COLORS.text, fontWeight:'700', flex:1 },
  summaryHead:{ flexDirection:'row', justifyContent:'space-between', alignItems:'flex-start', gap: 10 },
  summaryHeadNarrow: { flexDirection: 'column' },
  summaryTitle:{ fontWeight:'900', color: COLORS.text, fontSize: 16 },
  summaryRange:{ color: COLORS.muted, fontSize: 12, marginTop: 2 },
  summaryBig:{ fontWeight:'900', color: COLORS.text, fontSize: 18 },
  summarySub:{ color: COLORS.muted, fontSize: 12, marginTop: 2 },
  summaryTotalBlock: { alignItems:'flex-end', maxWidth: '52%' },
  summaryTotalBlockNarrow: { alignItems:'flex-start', maxWidth: '100%' },
  summarySectionHead: { marginTop: 10, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  summarySectionTitle: { color: '#1f2d1f', fontWeight: '900', fontSize: 13 },
  summarySectionCaption: { color: '#5f6f60', fontWeight: '700', fontSize: 11 },
  summaryCategoryCard: {
    marginTop: 8,
    borderWidth: 1,
    borderColor: '#d5e4d7',
    borderRadius: 12,
    backgroundColor: '#fbfefc',
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  iconPill:{ width:30, height:30, borderRadius: 15, borderWidth:1, borderColor: COLORS.accent, backgroundColor: COLORS.accentSoft, alignItems:'center', justifyContent:'center' },

  paretoRow:{ marginTop: 10, flexDirection:'row', alignItems:'center', columnGap:8 },
  paretoText:{ color: COLORS.muted, fontSize: 12, lineHeight: 16, flex:1 },

  sumRowNew:{ flexDirection:'row', alignItems:'center', paddingVertical: 8 },
  sumRowTop:{ flexDirection:'row', justifyContent:'space-between', alignItems:'center' },
  sumKeyGroup: { flex: 1, flexDirection: 'row', alignItems: 'center', paddingRight: 8, columnGap: 6 },
  topRankPill: {
    borderWidth: 1,
    borderColor: '#b7e6c5',
    backgroundColor: '#e9f8ef',
    borderRadius: 999,
    paddingHorizontal: 7,
    paddingVertical: 2,
  },
  topRankText: { color: COLORS.accent, fontWeight: '900', fontSize: 10 },
  sumKey:{ color: COLORS.text, fontWeight:'800', flex:1, paddingRight: 10 },
  sumAmt:{ color: COLORS.text, fontWeight:'800' },

  progressTrackSoft:{ height:8, borderRadius: 999, backgroundColor:'rgba(31,138,76,0.10)', overflow:'hidden', marginTop: 6 },
  progressFill:{ height:'100%', borderRadius: 999, backgroundColor: COLORS.accent },
  progressFillSky:{ height:'100%', borderRadius: 999, backgroundColor: COLORS.sky },

  sumSubText:{ color: COLORS.muted, fontSize: 12, marginTop: 6 },
  sumSubTopText: { marginTop: 4, color: '#335541', fontSize: 11, fontWeight: '700', lineHeight: 16 },

  // Insight block
  insightCard: {
    marginTop: 10,
    marginHorizontal: R.pad,
    borderWidth: 1,
    borderColor: '#cde7d0',
    backgroundColor: '#f4fbf6',
    borderRadius: R.radius,
    padding: R.pad,
    minHeight: 190,
    ...shadow,
  },
  insightHead: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 8,
  },
  insightIconWrap: {
    width: 30,
    height: 30,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#bce4c6',
    backgroundColor: '#e9f8ef',
    alignItems: 'center',
    justifyContent: 'center',
  },
  insightTitle: { color: PADIPRO.primaryDark, fontWeight: '900', fontSize: 15 },
  insightSub: { marginTop: 2, color: '#4a6455', fontSize: 12, fontWeight: '700' },
  insightFocusPill: {
    marginTop: 8,
    alignSelf: 'flex-start',
    borderWidth: 1,
    borderColor: '#c8e6d0',
    backgroundColor: '#eef9f2',
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 5,
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 6,
  },
  insightFocusText: { color: COLORS.accent, fontWeight: '800', fontSize: 11 },
  insightScopeText: { marginTop: 6, color: '#5b6f60', fontSize: 11, fontWeight: '700' },
  insightKpiRow: {
    marginTop: 10,
    flexDirection: 'row',
    columnGap: 8,
  },
  insightKpiItem: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#d4e8d9',
    backgroundColor: '#fbfefc',
    borderRadius: 12,
    paddingVertical: 8,
    paddingHorizontal: 10,
  },
  insightKpiLabel: {
    color: '#5b6f60',
    fontWeight: '800',
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.35,
  },
  insightKpiValue: { marginTop: 3, color: PADIPRO.primaryDark, fontWeight: '900', fontSize: 13 },
  insightRiskPill: {
    marginTop: 8,
    alignSelf: 'flex-start',
    borderWidth: 1,
    borderColor: '#bce4c6',
    backgroundColor: '#e9f8ef',
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 5,
  },
  insightRiskText: { color: COLORS.accent, fontWeight: '900', fontSize: 12 },
  insightTop3Wrap: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: '#d5e9da',
    backgroundColor: '#fbfefc',
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  insightTop3Row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#edf4ef',
  },
  insightTop3Rank: { width: 18, color: COLORS.accent, fontWeight: '900', fontSize: 12 },
  insightTop3Name: { flex: 1, color: '#254536', fontWeight: '800', fontSize: 12, paddingRight: 6 },
  insightTop3Pct: { width: 40, textAlign: 'right', color: COLORS.accent, fontWeight: '900', fontSize: 12 },
  insightTop3Amt: { width: 98, textAlign: 'right', color: PADIPRO.primaryDark, fontWeight: '900', fontSize: 12 },
  insightActionRow: {
    marginTop: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: 8,
  },
  insightBulletRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    columnGap: 8,
    marginTop: 0,
    flex: 1,
  },
  insightBulletDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    marginTop: 6,
    backgroundColor: COLORS.accent,
    flexShrink: 0,
  },
  insightBulletText: { flex: 1, color: '#2f4a3b', fontWeight: '700', fontSize: 12, lineHeight: 18 },
  insightDetailBtn: {
    borderWidth: 1,
    borderColor: '#c8e6d0',
    backgroundColor: '#eff9f3',
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  insightDetailBtnText: { color: COLORS.accent, fontWeight: '800', fontSize: 11 },

  // Pupuk focus
  pupukCard: {
    position: 'relative',
    overflow: 'hidden',
    marginHorizontal: R.pad,
    marginTop: 10,
    padding: R.pad,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: '#d9ceb2',
    backgroundColor: '#fbf7ea',
    shadowColor: '#2b1f05',
    shadowOpacity: 0.075,
    shadowRadius: 18,
    shadowOffset: { width: 0, height: 8 },
    elevation: 3,
  },
  pupukGlowA: {
    position: 'absolute',
    right: -42,
    top: -58,
    width: 138,
    height: 138,
    borderRadius: 69,
    backgroundColor: 'rgba(226, 203, 143, 0.32)',
  },
  pupukGlowB: {
    position: 'absolute',
    left: -58,
    bottom: -82,
    width: 160,
    height: 160,
    borderRadius: 80,
    backgroundColor: 'rgba(98, 132, 76, 0.10)',
  },
  pupukHead: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 10,
  },
  pupukHeadTiny: { flexDirection: 'column' },
  pupukTitleRow: { flex: 1, flexDirection: 'row', alignItems: 'center', columnGap: 9 },
  pupukIconPill: {
    width: 34,
    height: 34,
    borderRadius: 17,
    borderWidth: 1,
    borderColor: '#d8c991',
    backgroundColor: '#f7efd4',
    alignItems: 'center',
    justifyContent: 'center',
  },
  pupukTitle: { color: '#29331f', fontWeight: '900', fontSize: 16 },
  pupukSub: { marginTop: 2, color: '#6f674e', fontSize: 12, fontWeight: '700', lineHeight: 16 },
  pupukBadgeRow: {
    marginTop: 8,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  pupukAuditBadge: {
    borderWidth: 1,
    borderColor: '#d7d3a4',
    backgroundColor: 'rgba(249, 249, 231, 0.86)',
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 4,
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 4,
  },
  pupukAuditText: { color: '#52612f', fontWeight: '900', fontSize: 10 },
  pupukFilterBtn: {
    borderWidth: 1,
    borderColor: '#d7c78f',
    backgroundColor: '#fbf6e6',
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 7,
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 5,
    shadowColor: '#3d2a06',
    shadowOpacity: 0.05,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 3 },
    elevation: 1,
  },
  pupukFilterText: { color: '#5c512e', fontWeight: '900', fontSize: 11 },
  pupukMetricGrid: {
    marginTop: 12,
    flexDirection: 'row',
    flexWrap: 'wrap',
    columnGap: 8,
    rowGap: 8,
  },
  pupukMetricMain: {
    flexBasis: '100%',
    borderWidth: 1,
    borderColor: '#ddd2b7',
    backgroundColor: 'rgba(255, 254, 248, 0.84)',
    borderRadius: 15,
    paddingHorizontal: 12,
    paddingVertical: 11,
  },
  pupukMetricMini: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#e1d7be',
    backgroundColor: 'rgba(255, 254, 248, 0.70)',
    borderRadius: 13,
    paddingHorizontal: 9,
    paddingVertical: 10,
  },
  pupukMetricLabel: {
    color: '#756b55',
    fontWeight: '900',
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.35,
  },
  pupukMetricValue: { marginTop: 5, color: '#29331f', fontWeight: '900', fontSize: 17 },
  pupukMetricSmall: { marginTop: 5, color: '#29331f', fontWeight: '900', fontSize: 15 },
  pupukInsightStrip: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: '#ded2b5',
    backgroundColor: 'rgba(255, 254, 248, 0.66)',
    borderRadius: 13,
    paddingHorizontal: 10,
    paddingVertical: 8,
    flexDirection: 'row',
    alignItems: 'flex-start',
    columnGap: 8,
  },
  pupukInsightDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginTop: 5,
    backgroundColor: '#8a8f3a',
  },
  pupukInsightText: { flex: 1, color: '#69614b', fontWeight: '700', fontSize: 12, lineHeight: 17 },
  pupukInsightStrong: { color: '#29331f', fontWeight: '900' },
  pupukBusinessBox: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: '#ded3ba',
    backgroundColor: 'rgba(255, 254, 248, 0.78)',
    borderRadius: 16,
    paddingHorizontal: 10,
    paddingVertical: 9,
  },
  pupukBusinessHead: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    columnGap: 8,
    marginBottom: 4,
  },
  pupukBusinessTitle: { color: '#29331f', fontWeight: '900', fontSize: 13 },
  pupukBusinessSub: { marginTop: 2, color: '#756b55', fontWeight: '700', fontSize: 10 },
  pupukBusinessPill: {
    borderWidth: 1,
    borderColor: '#d7d3a4',
    backgroundColor: '#f9f7e7',
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  pupukBusinessPillText: { color: '#52612f', fontWeight: '900', fontSize: 10 },
  pupukBusinessRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(113, 103, 80, 0.09)',
  },
  pupukBusinessIcon: {
    width: 30,
    height: 30,
    borderRadius: 10,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 9,
  },
  pupukBusinessCopy: { flex: 1, minWidth: 0 },
  pupukBusinessLine: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: 8,
  },
  pupukBusinessName: { flex: 1, color: '#29331f', fontWeight: '900', fontSize: 12 },
  pupukBusinessAmount: { color: '#364029', fontWeight: '900', fontSize: 12 },
  pupukBusinessDesc: { marginTop: 2, color: '#756b55', fontWeight: '700', fontSize: 10 },
  pupukBusinessTrack: {
    marginTop: 6,
    height: 6,
    borderRadius: 999,
    backgroundColor: 'rgba(113, 103, 80, 0.12)',
    overflow: 'hidden',
  },
  pupukBusinessFill: { height: '100%', borderRadius: 999 },
  pupukBusinessPct: {
    marginLeft: 8,
    minWidth: 34,
    textAlign: 'right',
    color: '#68723d',
    fontWeight: '900',
    fontSize: 11,
  },
  pupukTopBox: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: '#ded3ba',
    backgroundColor: 'rgba(255, 254, 248, 0.76)',
    borderRadius: 15,
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  pupukTopHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 2,
  },
  pupukTopTitle: { color: '#29331f', fontWeight: '900', fontSize: 12 },
  pupukTopCaption: { color: '#756b55', fontWeight: '700', fontSize: 10 },
  pupukTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 7,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(113, 103, 80, 0.09)',
  },
  pupukRank: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: '#e7dfbd',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8,
  },
  pupukRankText: { color: '#52612f', fontWeight: '900', fontSize: 11 },
  pupukItemName: { color: '#29331f', fontWeight: '900', fontSize: 12 },
  pupukBarTrack: {
    marginTop: 5,
    height: 6,
    borderRadius: 999,
    backgroundColor: 'rgba(113, 103, 80, 0.12)',
    overflow: 'hidden',
  },
  pupukBarFill: { height: '100%', borderRadius: 999, backgroundColor: '#8a8f3a' },
  pupukAmountWrap: { alignItems: 'flex-end', minWidth: 86 },
  pupukAmount: { color: '#364029', fontWeight: '900', fontSize: 12 },
  pupukPct: { marginTop: 2, color: '#756b55', fontWeight: '800', fontSize: 10 },
  pupukEmptyText: { color: '#756b55', fontWeight: '700', fontSize: 12, lineHeight: 17, paddingVertical: 8 },

  // Pesticide block
  pestCard:{ borderWidth:1, borderColor: COLORS.sky, backgroundColor: COLORS.skySoft, borderRadius: R.radius, marginHorizontal: R.pad, marginTop: 10, padding: R.pad, minHeight: 190 },
  pestHead:{ marginBottom: 6 },
  pestTitle:{ fontWeight:'900', color: COLORS.text, fontSize: 16 },
  pestSub:{ color: COLORS.muted, fontSize: 12, marginTop: 2 },
  pestRowNew:{ flexDirection:'row', alignItems:'center', paddingVertical: 8, borderBottomWidth:1, borderBottomColor:'rgba(14,165,233,0.15)' },
  pestKind:{ color: COLORS.text, fontWeight:'800', textTransform:'capitalize', flex:1, paddingRight: 10 },

  // Item card
  card:{
    position: 'relative',
    overflow: 'hidden',
    borderWidth:1,
    borderColor: '#d8e6dc',
    borderRadius: 14,
    backgroundColor: '#ffffff',
    marginHorizontal: R.pad,
    marginTop: 9,
    padding: 13,
    shadowColor: '#0f2619',
    shadowOpacity: 0.055,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 4 },
    elevation: 2,
  },
  cardMobile: {
    marginTop: 8,
    padding: 12,
  },
  cardAccent: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 4,
    backgroundColor: '#1f8a4c',
    opacity: 0.75,
  },
  cardHeadRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: 9,
  },
  cardHeadRowTiny: {
    alignItems: 'stretch',
    flexDirection: 'column',
  },
  itemSourceWrap: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingRight: 10,
    columnGap: 8,
  },
  sourceBadge: {
    borderWidth: 1,
    borderColor: '#c9e4d0',
    backgroundColor: '#f3fbf5',
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  sourceBadgeText: {
    color: '#315341',
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.3,
    textTransform: 'uppercase',
  },
  itemIconPill: {
    width: 30,
    height: 30,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#c9e4d0',
    backgroundColor: '#edf8f0',
    alignItems: 'center',
    justifyContent: 'center',
  },
  itemBody: {
    paddingLeft: 2,
  },
  itemTitle:{ fontWeight:'900', color: COLORS.text, fontSize: 15, letterSpacing: 0.1 },
  categoryLine: { marginTop: 5, color: '#315341', fontWeight: '800', fontSize: 12 },
  itemMeta:{ color: COLORS.muted, marginTop: 4, fontWeight: '700', fontSize: 12 },
  itemNote:{ color: '#3f5645', marginTop: 4, fontSize: 12 },
  amountBox: {
    alignItems: 'flex-end',
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: '#d9e7dc',
    backgroundColor: '#fbfefc',
    borderRadius: 10,
    minWidth: 102,
  },
  amountBoxTiny: {
    alignItems: 'flex-start',
    minWidth: 0,
    width: '100%',
  },
  amountLabel: {
    color: '#5b6f60',
    fontSize: 10,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  amount:{ marginTop: 2, fontWeight:'900', color: COLORS.text, fontSize: 13.5 },
  qtyPrice:{
    alignSelf: 'flex-start',
    marginTop: 9,
    color: COLORS.text,
    fontWeight: '800',
    borderWidth: 1,
    borderColor: '#e0ece4',
    backgroundColor: '#fbfefc',
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 4,
    fontSize: 12,
  },
  allocationSummaryBox: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: '#dbe9df',
    backgroundColor: '#f8fcf9',
    borderRadius: 12,
    padding: 10,
  },
  allocationSummaryHead: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 6,
  },
  allocationSummaryTitle: {
    flex: 1,
    color: '#315341',
    fontWeight: '800',
    fontSize: 12,
  },
  allocationFieldsRow: {
    marginTop: 8,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  allocationFieldPill: {
    maxWidth: '48%',
    borderWidth: 1,
    borderColor: '#d6e6da',
    backgroundColor: '#ffffff',
    borderRadius: 999,
    paddingHorizontal: 9,
    paddingVertical: 4,
  },
  allocationFieldText: {
    color: '#315341',
    fontWeight: '800',
    fontSize: 11,
  },
  allocationFieldPillMore: {
    borderWidth: 1,
    borderColor: '#b7e6c5',
    backgroundColor: '#e9f8ef',
    borderRadius: 999,
    paddingHorizontal: 9,
    paddingVertical: 4,
  },
  allocationFieldMoreText: {
    color: COLORS.accent,
    fontWeight: '900',
    fontSize: 11,
  },
  allocationDetailToggle: {
    marginTop: 9,
    borderTopWidth: 1,
    borderTopColor: '#e2eee6',
    paddingTop: 9,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: 10,
  },
  allocationDetailToggleText: {
    color: '#113f2a',
    fontWeight: '900',
    fontSize: 12,
  },
  allocationDetailToggleSub: {
    marginTop: 2,
    color: '#6a7d70',
    fontWeight: '700',
    fontSize: 11,
  },
  allocationDetailList: {
    marginTop: 8,
    borderWidth: 1,
    borderColor: '#dcebe1',
    backgroundColor: '#ffffff',
    borderRadius: 11,
    overflow: 'hidden',
  },
  allocationDetailRow: {
    minHeight: 44,
    paddingHorizontal: 10,
    paddingVertical: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#edf4ef',
  },
  allocationDetailCopy: {
    flex: 1,
    minWidth: 0,
  },
  allocationDetailName: {
    color: COLORS.text,
    fontWeight: '900',
    fontSize: 12,
  },
  allocationDetailMeta: {
    marginTop: 2,
    color: '#6a7d70',
    fontWeight: '700',
    fontSize: 11,
  },
  allocationDetailAmt: {
    color: PADIPRO.primaryDark,
    fontWeight: '900',
    fontSize: 12,
    textAlign: 'right',
  },
  badgesRow:{ flexDirection:'row', columnGap:8, marginTop: 10 },
  badge:{ flexDirection:'row', alignItems:'center', columnGap:6, paddingVertical:4, paddingHorizontal:10, backgroundColor: '#f3fbf5', borderColor: '#c9e4d0', borderWidth:1, borderRadius: 999 },
  badgeText:{ color: COLORS.accent, fontWeight:'700', fontSize: 12 },
  actionRow: {
    marginTop: 10,
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  splitBtn: {
    borderWidth: 1,
    borderColor: '#b7e6c5',
    backgroundColor: '#e9f8ef',
    borderRadius: 999,
    paddingHorizontal: 13,
    paddingVertical: 8,
    flexDirection: 'row',
    alignItems: 'center',
  },
  splitBtnText: {
    marginLeft: 6,
    color: COLORS.accent,
    fontWeight: '800',
    fontSize: 12,
  },

  // Misc
  loadingWrap:{ flex:1, alignItems:'center', justifyContent:'center' },
  empty:{ alignItems:'center', marginTop: 24 },

  // Split Modal
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.3)',
    justifyContent: 'center',
    padding: 16,
  },
  modalCard: {
    backgroundColor: '#fff',
    borderRadius: 18,
    borderWidth: 1,
    borderColor: '#d6e6d8',
    padding: 16,
    maxWidth: 720,
    maxHeight: '92%',
  },
  modalCardNarrow: {
    borderRadius: 14,
    padding: 12,
    maxHeight: '94%',
  },
  modalHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  modalTitle: { color: COLORS.text, fontWeight: '900', fontSize: 19 },
  modalClose: {
    width: 28,
    height: 28,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#d4e4d6',
    backgroundColor: '#f7fbf8',
  },
  modalSub: { marginTop: 4, color: COLORS.muted, fontWeight: '700' },
  modalBodyScroll: {
    marginTop: 8,
    flexShrink: 1,
    minHeight: 0,
  },
  modalBodyContent: {
    paddingBottom: 8,
  },
  modalSectionLabel: {
    marginTop: 2,
    color: '#335541',
    fontSize: 11,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 0.45,
  },
  modalCategoryRow: {
    columnGap: 8,
    paddingTop: 8,
    paddingBottom: 2,
  },
  modalCategoryChip: {
    borderWidth: 1,
    borderColor: '#d4e4d6',
    borderRadius: 999,
    backgroundColor: '#fff',
    paddingHorizontal: 11,
    paddingVertical: 7,
  },
  modalCategoryChipActive: {
    borderColor: COLORS.accent,
    backgroundColor: '#e9f8ef',
  },
  modalCategoryChipText: {
    color: '#305241',
    fontWeight: '800',
    fontSize: 12,
  },
  modalCategoryChipTextActive: {
    color: COLORS.accent,
  },
  modalStatsRow: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: '#d4e5d8',
    borderRadius: 12,
    backgroundColor: '#f4faf6',
    paddingVertical: 8,
    paddingHorizontal: 10,
    flexDirection: 'row',
    alignItems: 'center',
  },
  modalStatItem: { flex: 1 },
  modalStatLabel: {
    color: '#5a6f60',
    fontSize: 10,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  modalStatValue: {
    marginTop: 2,
    color: COLORS.text,
    fontWeight: '900',
  },
  modalStatDivider: {
    width: 1,
    alignSelf: 'stretch',
    backgroundColor: '#d8e8dc',
    marginHorizontal: 8,
  },
  modalSwitchRow: { flexDirection: 'row', columnGap: 8, marginTop: 10 },
  modalListWrap: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: '#d6e6d8',
    borderRadius: 12,
    backgroundColor: '#fcfefc',
    padding: 10,
    minHeight: 140,
    maxHeight: 280,
  },
  modalToolsRow: {
    flexDirection: 'column',
    alignItems: 'stretch',
    marginBottom: 10,
    rowGap: 8,
  },
  modalSearchWrap: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#d4e5d8',
    borderRadius: 10,
    backgroundColor: '#fff',
    paddingHorizontal: 10,
    paddingVertical: 8,
    flexDirection: 'row',
    alignItems: 'center',
  },
  modalSearchInput: {
    flex: 1,
    color: COLORS.text,
    marginLeft: 6,
    fontSize: 13,
  },
  modalToolBtn: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#b8e5c8',
    backgroundColor: '#e9f8ef',
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 7,
    alignItems: 'center',
  },
  modalToolBtnText: {
    color: COLORS.accent,
    fontSize: 11,
    fontWeight: '800',
  },
  modalToolBtnGhost: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#d1ded4',
    backgroundColor: '#fff',
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 7,
    alignItems: 'center',
  },
  modalToolBtnGhostText: {
    color: '#466050',
    fontSize: 11,
    fontWeight: '800',
  },
  modalToolsActionRow: {
    flexDirection: 'row',
    columnGap: 8,
  },
  modalLoadingBox: { alignItems: 'center', justifyContent: 'center', paddingVertical: 20 },
  modalLoadingText: { marginTop: 8, color: COLORS.muted, fontWeight: '700' },
  modalEmptyBox: { alignItems: 'center', justifyContent: 'center', paddingVertical: 20 },
  modalEmptyText: { color: COLORS.muted, fontWeight: '700' },
  modalFieldRow: {
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#d4e4d6',
    borderRadius: 10,
    backgroundColor: '#fff',
    padding: 10,
    flexDirection: 'row',
    alignItems: 'center',
  },
  modalFieldRowActive: {
    borderColor: COLORS.accent,
    backgroundColor: COLORS.accentSoft,
  },
  modalFieldName: { color: COLORS.text, fontWeight: '800' },
  modalFieldMeta: { marginTop: 2, color: COLORS.muted, fontSize: 12, fontWeight: '700' },
  modalPreviewBox: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: '#cfe1d4',
    borderRadius: 12,
    backgroundColor: '#f3fbf5',
    padding: 10,
  },
  modalPreviewTitle: { color: COLORS.text, fontWeight: '900' },
  modalPreviewHeadGrid: {
    marginTop: 8,
    rowGap: 8,
  },
  modalPreviewHeadItem: {
    borderWidth: 1,
    borderColor: '#d2e7d8',
    borderRadius: 10,
    backgroundColor: '#fbfefc',
    paddingVertical: 8,
    paddingHorizontal: 10,
  },
  modalPreviewHeadItemWide: {
    borderWidth: 1,
    borderColor: '#d2e7d8',
    borderRadius: 10,
    backgroundColor: '#fbfefc',
    paddingVertical: 8,
    paddingHorizontal: 10,
  },
  modalPreviewHeadLabel: { color: '#5a6f60', fontWeight: '700', fontSize: 11 },
  modalPreviewHeadValue: { marginTop: 2, color: COLORS.accent, fontWeight: '900', fontSize: 14 },
  modalPreviewRow: {
    marginTop: 6,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  modalPreviewName: { color: COLORS.text, fontWeight: '700', flex: 1, paddingRight: 8 },
  modalPreviewMeta: { marginTop: 2, color: COLORS.muted, fontSize: 11, fontWeight: '700' },
  modalPreviewAmt: { color: COLORS.text, fontWeight: '900' },
  modalPreviewTotalRow: {
    marginTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#d8eadc',
    paddingTop: 7,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  modalPreviewTotalLabel: { color: '#305241', fontWeight: '800', fontSize: 12 },
  modalPreviewTotalAmt: { color: COLORS.accent, fontWeight: '900' },
  modalPreviewDeltaAmt: { color: '#1f2d1f', fontWeight: '900' },
  modalPreviewAssist: { marginTop: 4, color: '#4a6455', fontSize: 11, fontWeight: '700' },
  modalPreviewWarn: {
    marginTop: 8,
    color: '#b45309',
    fontSize: 12,
    fontWeight: '700',
  },
  modalActionRow: {
    marginTop: 10,
    flexDirection: 'row',
    justifyContent: 'space-between',
    columnGap: 10,
    borderTopWidth: 1,
    borderTopColor: '#d9e8dc',
    paddingTop: 10,
    backgroundColor: '#fff',
  },
  modalBtnGhost: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#cfe1d4',
    backgroundColor: '#fff',
    borderRadius: 10,
    paddingVertical: 11,
    alignItems: 'center',
  },
  modalBtnGhostText: { color: '#335541', fontWeight: '800' },
  modalBtnPrimary: {
    flex: 1.2,
    backgroundColor: COLORS.accent,
    borderRadius: 10,
    paddingVertical: 11,
    alignItems: 'center',
  },
  modalBtnPrimaryText: { color: '#fff', fontWeight: '900' },

  fab:{
    position:'absolute',
    right:16,
    bottom:16,
    width:56,
    height:56,
    borderRadius:28,
    backgroundColor: COLORS.accent,
    alignItems:'center',
    justifyContent:'center',
    ...padiproShadowLg,
  }
});
